import _ from "lodash";
import React from "react";
import { colors } from "@toddle-edu/ds-theme";
import moment from "moment";
import ACLStore from "lib/aclStore";
import {
  getDaysNameOrIndexArray,
  groupRotationDaysMemoize,
  getInitials,
  getDataGridRandomAvatarColor,
} from "Utils";
import { Avatar } from "@toddle-edu/ds-web";
import { getRotationDayRangeMemoize } from "AttendanceManager/modules/AttendanceManagerModule";
import { ATTENDANCE_GLOBAL_CONSTANTS } from "modules/Services";
import { getSettingValue } from "modules/PermissionModule";
import { ATTENDANCE_CONSTANTS } from "AttendanceDashboard/constants/stringConstants";
import { ATTENDANCE_CONSTANTS as CONSTANTS } from "Constants/attendanceConstants";
import { STUDENT_FULL_NAME } from "Constants/stringConstants";
import {
  getFormattedStartDate,
  getFormattedEndDate,
  formatDate,
  getTodaysDate,
  checkIsDateSame,
} from "Utils/DateTimeHelpers";
import {
  getFormattedUserName,
  getUserSortOptions,
} from "Utils/GetFormattedUserName";
import { DATE_TIME_CONSTANTS } from "Constants/dateTimeConstants";
import { isAdminTimeTableEnabled as checkAdminTimeTableEnabled } from "AdminTimetable/modules/Utils";
import { getIsAttendanceCategoryColorEnabled } from "AttendanceSettingsV2/modules/Utils";
import { getUserArchivalStatus } from "modules/CommonSelectors";
import { t } from "i18next";
import {
  EnvelopePlainOutlined,
  NotificationBellOutlined,
  StudentEditOutlined,
} from "@toddle-edu/ds-icons";
import ACLStorage from "lib/aclService";
import {
  ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION,
  ATTENDANCE_DASHBOARD_ACCESS_PERMISSION,
  HOMEROOM_ACCESS_PERMISSION,
  ATTENDANCE_SETTINGS_ACCESS_PERMISSION,
  ATTENDANCE_SETTINGS_V2_ACCESS_PERMISSION,
  CAN_MANAGE_TIMETABLE_CONFIGURATIONS_PERMISSION,
  MANAGE_TIMETABLE_MANAGER_PERMISSION,
  CAN_VIEW_TIMETABLE_MODULE_PERMISSION,
} from "Constants/permissionConstants";

const dateFormat = "YYYY-MM-DD";
const MAX_READABLE_TEXT_LENGTH = 94;

export const AVATAR_COLOR = [
  "neutral",
  "violet",
  "blue",
  "yellow",
  "pink",
  "teal",
];

export const sendReminderInitialObj = {
  checkedCourseIds: [], // Explicitly selected course ids
  selectedCourseIds: [], // To track the selected course ids based on different scenarios
  deSelectedCourseIds: [], // De selected course ids when any status category is enabled
  activeDropdownOption: null, // Status category selected
  availableClassesCount: 0, // Count of classes available on the count
};

export const studentCheckboxInitialObj = {
  checkedStudentIds: [], // Explicitly selected student ids
  selectedStudentIds: [], // To track the selected student ids based on different scenarios
  deSelectedStudentIds: [], // De selected student ids
  activeDropdownOption: null, // Status category selected
  availableStudentsCount: 0, // Count of students available on the count
};

const dateViewTypes = ["DAILY", "WEEKLY", "ROTATION_CYCLE"];

export const ATTENDANCE_REPORT_FILE_NAME = "Attendance report";

export const studentTabSortOptionsV2 = [
  {
    key: ATTENDANCE_CONSTANTS.FIRST_NAME_AZ,
    label: "common:first_name_az",
    direction: ATTENDANCE_CONSTANTS.ASC,
    sortEntity: ATTENDANCE_CONSTANTS.FIRST_NAME,
  },
  {
    key: ATTENDANCE_CONSTANTS.FIRST_NAME_ZA,
    label: "common:first_name_za",
    direction: ATTENDANCE_CONSTANTS.DESC,
    sortEntity: ATTENDANCE_CONSTANTS.FIRST_NAME,
  },
  {
    key: ATTENDANCE_CONSTANTS.MIDDLE_NAME_AZ,
    label: "common:middle_name_az",
    direction: ATTENDANCE_CONSTANTS.ASC,
    sortEntity: ATTENDANCE_CONSTANTS.MIDDLE_NAME,
  },
  {
    key: ATTENDANCE_CONSTANTS.MIDDLE_NAME_ZA,
    label: "common:middle_name_za",
    direction: ATTENDANCE_CONSTANTS.DESC,
    sortEntity: ATTENDANCE_CONSTANTS.MIDDLE_NAME,
  },
  {
    key: ATTENDANCE_CONSTANTS.LAST_NAME_AZ,
    label: "common:last_name_az",
    direction: ATTENDANCE_CONSTANTS.ASC,
    sortEntity: ATTENDANCE_CONSTANTS.LAST_NAME,
  },
  {
    key: ATTENDANCE_CONSTANTS.LAST_NAME_ZA,
    label: "common:last_name_za",
    direction: ATTENDANCE_CONSTANTS.DESC,
    sortEntity: ATTENDANCE_CONSTANTS.LAST_NAME,
  },
  {
    key: ATTENDANCE_CONSTANTS.FULL_NAME_AZ,
    label: "common:full_name_az",
    direction: ATTENDANCE_CONSTANTS.ASC,
    sortEntity: ATTENDANCE_CONSTANTS.FULL_NAME,
  },
  {
    key: ATTENDANCE_CONSTANTS.FULL_NAME_ZA,
    label: "common:full_name_za",
    direction: ATTENDANCE_CONSTANTS.DESC,
    sortEntity: ATTENDANCE_CONSTANTS.FULL_NAME,
  },
];

const excusalsDateViewTypeOptionsMapping = [
  {
    key: ATTENDANCE_CONSTANTS.WEEKLY,
    label: "common:weekly",
  },
  {
    key: ATTENDANCE_CONSTANTS.MONTHLY,
    label: "common:monthly",
  },
  {
    key: ATTENDANCE_CONSTANTS.YEARLY,
    label: "common:yearly",
  },
];

export const getClassActionDropdownOptions = ({
  markAttendancePermission,
  canSendReminderAndEditTeacherPermission,
}) => {
  const actionDropdownOptions = [];

  if (markAttendancePermission) {
    const editAttendance = {
      key: "mark_attendance",
      editAttendanceLabel: "attendance:edit_attendance",
      markAttendanceLabel: "attendance:mark_attendance",
      icon: "StudentEditOutlined",
    };

    actionDropdownOptions.push(editAttendance);
  }

  if (canSendReminderAndEditTeacherPermission) {
    const assignTeacher = {
      key: "assign_teacher",
      label: "attendance:assign_teacher",
      icon: "SetupOutlined",
    };

    const sendReminder = {
      key: "send_reminder",
      label: "attendance:send_reminder",
      icon: "EnvelopePlainOutlined",
    };
    actionDropdownOptions.push(assignTeacher);
    actionDropdownOptions.push(sendReminder);
  }

  return actionDropdownOptions;
};

const ATTENDANCE_DETAIL_HOMEROOM_ONCE_A_DAY = [
  {
    key: ATTENDANCE_CONSTANTS.MARKED,
    label: "attendance:homerooms_marked",
    textColor: colors.black,
    dataKey: "markedAttendanceStaff",
  },
  {
    key: ATTENDANCE_CONSTANTS.NOT_MARKED,
    label: "attendance:homerooms_not_marked",
    textColor: colors.textCritical,
    dataKey: "notMarkedAttendanceStaff",
  },
];

const getDailyAttendanceWithClasses = ({ isHomeroomClassesEnabled }) => {
  if (isHomeroomClassesEnabled) {
    return [
      {
        key: "ALL",
        label: "Homerooms Marked",
        textColor: colors.black,
        dataKey: "markedCoursesCount",
      },
      {
        key: ATTENDANCE_CONSTANTS.NOT_MARKED,
        label: "Homerooms not marked",
        textColor: colors.textCritical,
        dataKey: "nonMarkedCoursesCount",
      },
    ];
  }

  return [
    {
      key: "ALL",
      label: "Classes Marked",
      textColor: colors.black,
      dataKey: "markedCoursesCount",
    },
    {
      key: ATTENDANCE_CONSTANTS.NOT_MARKED,
      label: "Classes not marked",
      textColor: colors.textCritical,
      dataKey: "nonMarkedCoursesCount",
    },
  ];
};

const ATTENDANCE_DETAIL_OPTION_ONCE_A_DAY = ({ t }) => [
  {
    key: "MARKED",
    label: "attendance:classes_marked",
    statusLabel: t("attendance:marked"),
    textColor: colors.black,
  },
  {
    key: "NOT_MARKED",
    label: "attendance:classes_not_marked",
    statusLabel: t("attendance:not_marked"),
    textColor: colors.textCritical,
  },
  {
    key: "PARTIALLY_MARKED",
    label: "attendance:classes_partially_marked",
    statusLabel: t("attendance:partially_marked"),
    textColor: colors.textWarning,
  },
];

const getAttendanceOverviewTabOptions = ({ studentsAttendanceOverview }) => {
  if (getAttendanceTiersEnabled()) {
    const { tierSummary, overallPresenceAvg } = _.get(
      studentsAttendanceOverview,
      "tierSummary",
      {}
    );

    const attendanceOverviewTabOptions = _.map(
      tierSummary,
      ({ tier, count }) => {
        const { color, label, id, minPercentage, maxPercentage } = tier || {};
        const baseColor = color?.split("-")?.slice(-2, -1)[0] || "";
        const fontColor = `var(--tag-foreground-${baseColor})`;
        return {
          key: id,
          subLabel: label,
          textColor: fontColor,
          studentCount: count,
          minPercentage,
          maxPercentage,
          isAttendanceTier: true,
        };
      }
    );

    return [
      {
        key: ATTENDANCE_CONSTANTS.OVERALL_PRESENCE,
        studentCount: overallPresenceAvg,
        subLabel: "Average Presence",
        textColor: colors.black,
      },
      ...attendanceOverviewTabOptions,
    ];
  }

  return [
    {
      key: ATTENDANCE_CONSTANTS.OVERALL_PRESENCE,
      subLabel: "attendance:attendance_percentage",
      textColor: colors.black,
    },
    {
      key: ATTENDANCE_CONSTANTS.PERFECT_ATTENDANCE,
      subLabel: "attendance:with_perfect_attendance",
      textColor: colors.textLink,
    },
    {
      key: ATTENDANCE_CONSTANTS.MORE_THAN_90,
      subLabel: "attendance:with_more_than_percent_presence",
      textColor: colors.textLink,
    },
    {
      key: ATTENDANCE_CONSTANTS.LESS_THAN_90,
      subLabel: "attendance:with_less_than_percent_presence",
      textColor: colors.textCritical,
    },
  ];
};

export const isNetworkStatusLoading = networkStatus => {
  return networkStatus === 3;
};

export const checkIsToddleV2TimetableEnabled = () => {
  return ACLStore.can("FeatureFlag:Toddle2.0Timetable");
};

export const checkIsToddleV2AttendanceEnabled = () => {
  return ACLStore.can("FeatureFlag:Toddle2.0Attendance");
};

export const checkAttendanceExcusalVisible = () => {
  return ACLStore.can("Common:AttendanceExcusals");
};

export const checkWeightedAttendanceCategoriesEnabled = () => {
  return ACLStore.can("FeatureFlag:WeightedAttendanceCategories");
};

export const checkSMSSendVisible = () => {
  return ACLStore.can("Common:AttendanceSMS");
};

export const checkAttendanceSMSConfirmationModalVisible = () => {
  return ACLStore.can("Common:AttendanceSMSConfirmationModal");
};

export const checkWeightedAttendanceCategoriesVisible = () => {
  return ACLStore.can("FeatureFlag:WeightedAttendanceCategories");
};

export const checkHomeroomVisible = () => {
  return ACLStore.can("Common:Homeroom");
};

export const getMultiCurriculumAttendanceCategoriesEnabled = () => {
  return ACLStore.can("FeatureFlag:MultiCurriculumAttendanceCategories");
};

export const getAttendanceSettingsV2Enabled = () => {
  return ACLStore.can("FeatureFlag:AttendanceSettingsV2");
};

export const getAttendanceSettingsV2M2Enabled = () => {
  return ACLStore.can("FeatureFlag:AttendanceSettingsV2Milestone2");
};

export const getAttendanceHomeroomClassesEnabled = () => {
  return ACLStore.can("FeatureFlag:AttendanceHomeroomClasses");
};

export const getNewAttendanceDashboardsAndAnalyticsEnabled = () => {
  return (
    ACLStore.can("FeatureFlag:NewAttendanceDashboardsAndAnalytics") ||
    ACLStore.can("FeatureFlag:Toddle2.0Attendance")
  );
};

export const getAttendanceAutoMarkingNewFlowEnabled = () => {
  return ACLStore.can("FeatureFlag:AttendanceAutoMarkingNewFlow");
};

export const checkIsCalendarEventItemsEnabled = () => {
  return (
    ACLStore.can("FeatureFlag:EnableCalendarRevampPhase1") ||
    ACLStore.can("FeatureFlag:Toddle2.0Timetable")
  );
};

export const checkIsExcusalsModuleDecoupled = () => {
  return ACLStore.can("FeatureFlag:DecoupleExcusalModule");
};

export const getNonOperationalHolidayExcusalEnabled = () => {
  return ACLStore.can("FeatureFlag:NonOperationalHolidayExcusal");
};

export const getIsShowCommentsInExcusalEnabled = () => {
  return ACLStore.can("FeatureFlag:ShowCommentsInExcusal");
};

// Custom Feature Flags for CENT School - "189138035537920257"
export const getShowHomeroomAttendanceToggle = () => {
  return ACLStore.can("FeatureFlag:ShowHomeroomAttendanceToggle");
};

export const getShowHomeroomAttendance = ({ userId, curriculumProgramId }) => {
  return getShowHomeroomAttendanceToggle()
    ? getSettingValue({
        name: "ShowHomeroomAttendance",
        userId,
        currentCurriculumProgramId: curriculumProgramId,
      })
    : false; // Default to false when feature flag is disabled
};

export const checkIsMarkingModalNewStudentsSortingEnabled = () => {
  return ACLStore.can("FeatureFlag:MarkingModalNewStudentsSorting");
};

export const checkIsAttendanceDashboardFiltersHidden = () => {
  return ACLStore.can("FeatureFlag:HideFiltersInAttendanceDashboard");
};

export const getShowMarkedUnmarkedStudentCount = () => {
  return ACLStore.can("FeatureFlag:ShowMarkedUnmarkedStudentCount");
};

export const getAttendanceAssemblyRollEnabled = () => {
  return ACLStore.can("FeatureFlag:AttendanceAssemblyRollReport");
};

export const getIsStudentClassWiseAttendanceCountSummaryEnabled = () => {
  return ACLStore.can("FeatureFlag:StudentClassWiseAttendanceCountSummary");
};

export const getRealTimeAttendanceMarkingEnabled = () => {
  return ACLStore.can("FeatureFlag:EnableRealTimeAttendanceMarking");
};

export const getIsAllCurriculumHomeroomAttendanceSummaryEnabled = () => {
  return ACLStore.can("FeatureFlag:AllCurriculumHomeroomAttendanceSummary");
};

export const checkIsYearGroupGradeForRoutineEnabled = () => {
  return ACLStore.can("FeatureFlag:UseYearGroupGradeForRoutine");
};

export const checkIsStudentListPaginationEnhancementEnabled = () => {
  return ACLStore.can("FeatureFlag:StudentListPaginationEnhancement");
};

export const checkIsFireDrillAttendanceReportEnabled = () => {
  return ACLStore.can("FeatureFlag:FireDrillAttendanceReport");
};

export const getCanManageTimetableConfigurations = ({
  isAccountPortal,
  curriculumProgramId,
  fromTimetableManager,
}) => {
  return fromTimetableManager
    ? ACLStorage.getPermissionValue(MANAGE_TIMETABLE_MANAGER_PERMISSION)
    : ACLStorage.getPermissionValueByContext(
        CAN_MANAGE_TIMETABLE_CONFIGURATIONS_PERMISSION,
        [
          {
            entityId: curriculumProgramId,
            entityType: "CURRICULUM_PROGRAM",
          },
        ]
      );
};

export const getAttendanceTiersEnabled = () => {
  return ACLStore.can("FeatureFlag:AttendanceTiers");
};

const getMultiCurriculumAttendanceOptionsVariables = ({
  organizationId,
  curriculumProgramId,
  selectedAcademicYearId,
}) => {
  return {
    id: organizationId,
    organizationAttendanceFilters: {
      curriculumProgramIds: [curriculumProgramId],
      academicYearIds: [selectedAcademicYearId],
    },
  };
};

export const getMultiCurriculumAttendanceOptionsVariablesMemoized = _.memoize(
  params => getMultiCurriculumAttendanceOptionsVariables(params),
  ({ organizationId, curriculumProgramId, selectedAcademicYearId }) => {
    return `${organizationId}-${curriculumProgramId}-${selectedAcademicYearId}`;
  }
);

const getShouldAddHomeroomTab = ({
  attendanceRecordType,
  curriculumDefaultPeriod,
  isAttendanceOnceADay,
  viewHomeroomAttendancePermission,
  homeroomLabel,
  isAttendanceHomeroomClassesEnabled,
  dailyRecordingMethod,
}) => {
  const isFeatureFlagEnabled =
    getAttendanceSettingsV2M2Enabled() || checkIsToddleV2AttendanceEnabled();

  if (
    !isFeatureFlagEnabled ||
    attendanceRecordType === "EACH_PERIOD" ||
    !viewHomeroomAttendancePermission
  ) {
    return [];
  }

  return [
    {
      label: homeroomLabel,
      value: ATTENDANCE_CONSTANTS.HOMEROOM_UPPERCASE,
    },
  ];
};

const getAttendanceDashboardTabs = ({
  isHomeroomTabEnabled,
  viewHomeroomAttendancePermission,
  allClassesRecordingType,
  isScheduledPeriodEnabled,
  homeroomLabel,
  classesLabel,
  shouldAddHomeroomTab,
  dailyRecordingMethod,
  isAttendanceHomeroomClassesEnabled,
  isHomeroomClassesEnabled,
  isAttendanceOnceADay,
  attendanceRecordType,
  t,
}) => {
  const tabs = [];

  const isEachPeriodWithDailyAttendance =
    attendanceRecordType === "EACH_PERIOD_WITH_ONCE_A_DAY";

  if (isAttendanceHomeroomClassesEnabled) {
    if (isEachPeriodWithDailyAttendance) tabs.push(...shouldAddHomeroomTab);

    if (isHomeroomTabEnabled && viewHomeroomAttendancePermission) {
      tabs.push({
        label:
          !allClassesRecordingType && !isScheduledPeriodEnabled
            ? homeroomLabel
            : classesLabel,
        value: ATTENDANCE_CONSTANTS.CLASSES,
      });
    } else if (!isHomeroomTabEnabled) {
      tabs.push({
        label: classesLabel,
        value: ATTENDANCE_CONSTANTS.CLASSES,
      });
    }
  } else {
    if (!isAttendanceOnceADay) {
      tabs.push({
        label: classesLabel,
        value: ATTENDANCE_CONSTANTS.CLASSES,
      });
    }

    tabs.push(...shouldAddHomeroomTab);
  }

  // Add Students tab
  tabs.push({
    label: t("common:student_plural"),
    value: ATTENDANCE_CONSTANTS.STUDENTS,
  });

  // Add Excusals tab if visible
  if (checkAttendanceExcusalVisible()) {
    tabs.push({
      label: t("attendance:excusals"),
      value: ATTENDANCE_CONSTANTS.EXCUSALS,
    });
  }

  return tabs;
};

export const getHeaderTabs = ({
  t,
  isAttendanceOnceADay,
  attendanceRecordType,
  isHomeroomEnabled,
  curriculumDefaultPeriod,
  viewHomeroomAttendancePermission,
  isScheduledPeriodEnabled,
  allClassesRecordingType,
  dailyRecordingMethod,
  isAttendanceHomeroomClassesEnabled,
  isHomeroomClassesEnabled,
}) => {
  const isHomeroomTabEnabled = isAttendanceOnceADay && isHomeroomEnabled;

  const homeroomLabel = getAttendanceHomeroomClassesEnabled()
    ? t("common:daily")
    : t("attendance:homerooms");

  const classesLabel = getAttendanceHomeroomClassesEnabled()
    ? isAttendanceOnceADay
      ? t("common:daily")
      : t("common:period_plural")
    : t("common:class_plural");

  const shouldAddHomeroomTab = getShouldAddHomeroomTab({
    attendanceRecordType,
    curriculumDefaultPeriod,
    isAttendanceOnceADay,
    viewHomeroomAttendancePermission,
    homeroomLabel,
    dailyRecordingMethod,
    isAttendanceHomeroomClassesEnabled,
  });

  const attendanceDashboardTab = getAttendanceDashboardTabs({
    isHomeroomTabEnabled,
    viewHomeroomAttendancePermission,
    allClassesRecordingType,
    isScheduledPeriodEnabled,
    homeroomLabel,
    classesLabel,
    shouldAddHomeroomTab,
    dailyRecordingMethod,
    isAttendanceHomeroomClassesEnabled,
    isHomeroomClassesEnabled,
    isAttendanceOnceADay,
    attendanceRecordType,
    t,
  });

  return attendanceDashboardTab;
};

export const getAttendanceExcusalHeaderData = ({ t }) => {
  const studentSortOptions = getUserSortOptions({
    type: "student",
    sortOptions: studentTabSortOptionsV2,
    keyToMap: "sortEntity",
  });

  const dropdownOptions = _.map(
    studentSortOptions,
    ({ key, label, direction, sortEntity }) => {
      return {
        key,
        label: t(label),
        direction,
        sortEntity,
      };
    }
  );
  return [
    {
      key: ATTENDANCE_CONSTANTS.STUDENT,
      entity: ATTENDANCE_CONSTANTS.STUDENT,
      value: t("common:student_name"),
      sortOptions: dropdownOptions,
      sortType: "dropdown",
      isSticky: true,
    },
    {
      key: ATTENDANCE_CONSTANTS.DURATION,
      entity: ATTENDANCE_CONSTANTS.DURATION,
      value: t("common:duration"),
      style: {
        width: "180px",
      },
    },
    {
      key: ATTENDANCE_CONSTANTS.EXCUSAL,
      entity: ATTENDANCE_CONSTANTS.EXCUSAL,
      value: t("attendance:excusal"),
      style: {
        width: "352px",
      },
    },
    {
      key: ATTENDANCE_CONSTANTS.ATTENDANCE,
      entity: ATTENDANCE_CONSTANTS.ATTENDANCE,
      value: t("attendance:attendance"),
      style: {
        minWidth: "352px",
      },
    },
  ];
};
export const getStudentsListDropdownOptionsForAttendanceCountSorting = () => {
  return [
    {
      key: "DIVIDER",
      isDivider: true,
    },
    {
      key: ATTENDANCE_CONSTANTS.MARKED_FIRST,
      label: "attendance:marked_first",
      direction: ATTENDANCE_CONSTANTS.DESC,
      sortEntity: ATTENDANCE_CONSTANTS.ATTENDANCE_COUNT,
    },
    {
      key: ATTENDANCE_CONSTANTS.UNMARKED_FIRST,
      label: "attendance:unmarked_first",
      direction: ATTENDANCE_CONSTANTS.ASC,
      sortEntity: ATTENDANCE_CONSTANTS.ATTENDANCE_COUNT,
    },
  ];
};

export const getClassesDropdownOptions = ({ t }) => {
  return [
    {
      label: t("attendance:partially_marked"),
      key: "PARTIALLY_MARKED",
    },
    {
      label: t("attendance:not_marked"),
      key: "NOT_MARKED",
    },
  ];
};

export const getClassesReportDownloadDropdownOptions = ({
  selectedCourseIdsCount,
  periodLabel,
  t,
}) => {
  return selectedCourseIdsCount < 1
    ? [
        {
          key: "ALL_PERIODS",
          label: t("attendance:all_periods"),
          isItemGroup: true,
          options: [
            {
              key: "ALL_PERIODS_SUMMARY_REPORT",
              label: t("attendance:summary_report"),
              subText: t("attendance:all_periods_summary_report_subtext"),
            },
            {
              key: "ALL_PERIODS_ATTENDANCE_RECORDS",
              label: t("attendance:attendance_records"),
              subText: t("attendance:all_periods_attendance_record_subtext"),
            },
          ],
        },
        {
          key: "SINGLE_PERIOD",
          label: periodLabel,
          isItemGroup: true,
          options: [
            {
              key: "SINGLE_PERIOD_SUMMARY_REPORT",
              label: t("attendance:summary_report"),
              subText: t("attendance:single_period_summary_report_subtext", {
                periodLabel,
              }),
            },
            {
              key: "SINGLE_PERIOD_ATTENDANCE_RECORDS",
              label: t("attendance:attendance_records"),
              subText: t("attendance:single_period_attendance_record_subtext", {
                periodLabel,
              }),
            },
          ],
        },
      ]
    : [
        {
          key: "SELECTED_CLASSES",
          label: t("attendance:selected_classes"),
          isItemGroup: true,
          options: [
            {
              key: "SELECTED_CLASSES_SUMMARY_REPORT",
              label: t("attendance:summary_report"),
              subText: t("attendance:selected_classes_summary_report_subtext"),
            },
            {
              key: "SELECTED_CLASSES_ATTENDANCE_RECORDS",
              label: t("attendance:attendance_records"),
              subText: t(
                "attendance:selected_classes_attendance_record_subtext"
              ),
            },
          ],
        },
      ];
};

export const getClassesReportOnceADayDownloadDropdownOptions = ({
  selectedCourseIdsCount,
  t,
}) => {
  const dropdownOptions = [
    {
      key: ATTENDANCE_CONSTANTS.COURSE_ATTENDANCE_SUMMARY_REPORT,
      label: t("attendance:summary_report"),
      subText: t("attendance:all_periods_summary_report_subtext"),
    },
    {
      key: ATTENDANCE_CONSTANTS.COURSE_ATTENDANCE_RECORDS_REPORT,
      label: t("attendance:attendance_records"),
      subText: t("attendance:all_periods_attendance_record_subtext"),
    },
  ];
  return selectedCourseIdsCount < 1
    ? dropdownOptions
    : [
        {
          key: "SELECTED_CLASSES",
          label: t("attendance:selected_classes"),
          isItemGroup: true,
          options: dropdownOptions,
        },
      ];
};

export const getHomeroomReportDownloadDropdownOptions = ({
  t,
  selectedCourseIdsCount,
}) => {
  const dropdownOptions = [
    {
      key: ATTENDANCE_CONSTANTS.HOMEROOM_ATTENDANCE_SUMMARY_REPORT,
      label: t("attendance:summary_report"),
      subText: t("attendance:homeroom_summary_report_subtext"),
    },
    {
      key: ATTENDANCE_CONSTANTS.HOMEROOM_ATTENDANCE_RECORDS_REPORT,
      label: t("attendance:attendance_records"),
      subText: t("attendance:homeroom_attendance_record_subtext"),
    },
  ];
  return selectedCourseIdsCount < 1
    ? dropdownOptions
    : [
        {
          key: "SELECTED_HOMEROOMS",
          label: t("attendance:selected_homerooms"),
          isItemGroup: true,
          options: dropdownOptions,
        },
      ];
};

export const getStudentsReportDownloadDropdownOptions = ({
  isDailyView,
  isAttendanceOnceADay,
  selectedStudentIdsCount,
  t,
}) => {
  const attendanceRecordsSubText = isAttendanceOnceADay
    ? t("attendance:daily_attendance_records_of_students")
    : t("attendance:period_wise_attendance_records_of_students");
  const dropdownOptions = isDailyView
    ? [
        {
          key: ATTENDANCE_CONSTANTS.STUDENT_ATTENDANCE_RECORDS_REPORT,
          label: t("attendance:attendance_records"),
          subText: attendanceRecordsSubText,
        },
      ]
    : [
        {
          key: ATTENDANCE_CONSTANTS.STUDENT_ATTENDANCE_SUMMARY_REPORT,
          label: t("attendance:summary_report"),
          subText: t("attendance:students_summary_report_subtext"),
        },
        {
          key: ATTENDANCE_CONSTANTS.STUDENT_ATTENDANCE_RECORDS_REPORT,
          label: t("attendance:attendance_records"),
          subText: attendanceRecordsSubText,
        },
        {
          key: ATTENDANCE_CONSTANTS.STUDENT_INDIVIDUAL_ATTENDANCE_REPORT,
          label: t("attendance:individual_attendance_reports"),
          subText: t("attendance:individual_attendance_report_subtext"),
        },
      ];
  return selectedStudentIdsCount < 1
    ? dropdownOptions
    : [
        {
          key: "SELECTED_STUDENTS",
          label: t("attendance:selected_students"),
          isItemGroup: true,
          options: dropdownOptions,
        },
      ];
};

export const getDescriptionForDisplay = ({
  description,
  toggleItem,
  showAllDescription,
  textClass,
  canShowMoreToggle,
  containerStyle,
}) => {
  if (_.size(description) <= MAX_READABLE_TEXT_LENGTH || !canShowMoreToggle) {
    return (
      <pre className={textClass} dir="auto">
        {description}
      </pre>
    );
  }

  const slicedDescription = _.join(
    _.slice([...description], 0, MAX_READABLE_TEXT_LENGTH),
    ""
  );

  return (
    <span className={containerStyle}>
      <pre className={textClass} dir="auto">
        {showAllDescription ? description : `${slicedDescription}...`}
        {toggleItem}
      </pre>
    </span>
  );
};

export const checkForAnyCategoryEmailSelected = ({ options }) => {
  return _.some(options, ["sendEmail", true]);
};

export const commonObjectKeys = {
  alignment: "center",
  entity: "",
  isSticky: false,
  sortOptions: [],
  sortType: "",
  style: {},
  uiType: "Text",
};

export const getExcusalMenuItem = ({ t }) => {
  return [
    {
      key: ATTENDANCE_CONSTANTS.NEW_EXCUSALS,
      label: t("attendance:new_excusals"),
    },
    {
      key: ATTENDANCE_CONSTANTS.UPCOMING_AND_ONGOING_EXCUSALS,
      label: t("attendance:upcoming_and_ongoing_excusals"),
    },
    {
      key: ATTENDANCE_CONSTANTS.MARKED_EXCUSALS,
      label: t("attendance:marked_excusals"),
    },
  ];
};

export const commonStudentsObjectKey = {
  alignment: "center",
  entity: "",
  isSticky: false,
  sortOptions: [],
  sortType: "",
  style: { minWidth: "80px" },
  uiType: "MarkStudentAttendance",
};

export const dailyAttendanceObjectKey = {
  alignment: "center",
  entity: "",
  isSticky: false,
  sortOptions: [],
  sortType: "",
  style: {
    minWidth: "165px",
    padding: 0,
    borderRight: `1px solid var(--neutral-800)`,
    borderLeft: `1px solid var(--neutral-800)`,
  },
  uiType: ATTENDANCE_CONSTANTS.DAILY_ATTENDANCE_CAMEL_CASE,
};

export const dailyAttendanceKey = {
  alignment: "center",
  entity: "",
  isSticky: false,
  sortOptions: [],
  sortType: "",
  style: {
    // minWidth: "165px",
    padding: 0,
  },
  uiType: ATTENDANCE_CONSTANTS.DAILY_ATTENDANCE_CAMEL_CASE,
};

export const getStudentColumnObject = ({ t }) => {
  return {
    alignment: "left",
    entity: "STUDENT",
    isSticky: true,
    key: "student_name",
    sortOptions: [],
    sortType: "dropdown",
    style: {
      minWidth: "196px",
      width: "max-content",
    },
    value: t("common:student_name"),
    uiType: "AvatarWithSubText",
  };
};

export const studentExcusalObject = {
  alignment: "left",
  entity: ATTENDANCE_CONSTANTS.EXCUSAL,
  isSticky: false,
  sortOptions: [],
  style: { width: "420px" },
  uiType: ATTENDANCE_CONSTANTS.EXCUSAL,
};

export const getFormattedPeriodName = ({ periodsIndex, periods }) => {
  if (checkAdminTimeTableEnabled() && periods) {
    const periodsAbbreviation = _.map(
      periods,
      ({ abbreviation }) => abbreviation
    );
    return _.join(periodsAbbreviation, ", ");
  }

  let periodTextFormatted = "";
  const periodsIndexLength = _.size(periodsIndex);

  _.map(periodsIndex, (ind, index) => {
    if (index === periodsIndexLength - 1) {
      periodTextFormatted += `P${ind + 1}`;
    } else {
      periodTextFormatted += `P${ind + 1}, `;
    }
  });

  if (periodsIndexLength > 1) {
    let continuousPeriodCount = 1;
    for (var i = 1; i < periodsIndex.length; i++) {
      continuousPeriodCount += periodsIndex[i] === periodsIndex[i - 1] + 1;
    }
    if (continuousPeriodCount === periodsIndexLength) {
      periodTextFormatted = `P${periodsIndex[0] + 1} - P${
        periodsIndex[periodsIndexLength - 1] + 1
      }`;
    }
  }

  return periodTextFormatted;
};

export const getAttendanceExcusalStatus = isNewExcusal => {
  return isNewExcusal
    ? ATTENDANCE_CONSTANTS.PENDING
    : ATTENDANCE_CONSTANTS.MARKED;
};

export const checkIsNewExcusal = key => {
  return key === ATTENDANCE_CONSTANTS.NEW_EXCUSALS;
};

export const getStatusByDuration = isOngoingExcusal => {
  return isOngoingExcusal ? ATTENDANCE_CONSTANTS.UPCOMING : undefined;
};

export const getDataInDDDFormat = date => {
  return moment(date).locale("en").format(ATTENDANCE_CONSTANTS.DDD);
};

export const getOnceADayExcusalData = ({
  isPeriodAttendanceView,
  isExcusalIsInBetween,
  excusalData,
}) => {
  return isPeriodAttendanceView
    ? isExcusalIsInBetween
      ? excusalData
      : null
    : excusalData;
};

export const getExcusalDurationTime = ({
  durationType,
  startDate,
  endDate,
}) => {
  switch (durationType) {
    case ATTENDANCE_CONSTANTS.SINGLE_DAY:
    case ATTENDANCE_CONSTANTS.PARTIAL_DAY:
      return formatDate({
        date: startDate,
        format: ATTENDANCE_CONSTANTS.EXCUSAL_DATE_FORMAT,
      });

    case ATTENDANCE_CONSTANTS.MULTIPLE_DAYS:
      return `${formatDate({
        date: startDate,
        format: ATTENDANCE_CONSTANTS.EXCUSAL_DATE_FORMAT,
      })} - ${formatDate({
        date: endDate,
        format: ATTENDANCE_CONSTANTS.EXCUSAL_DATE_FORMAT,
      })}`;
  }
};

export const getExcusalCountNumber = ({ key, excusalCounts }) => {
  switch (key) {
    case ATTENDANCE_CONSTANTS.NEW_EXCUSALS:
      return _.get(excusalCounts, "newExcusals.totalCount", 0);

    case ATTENDANCE_CONSTANTS.UPCOMING_AND_ONGOING_EXCUSALS:
      return _.get(excusalCounts, "upcomingAndOngoingExcusals.totalCount", 0);

    case ATTENDANCE_CONSTANTS.MARKED_EXCUSALS:
      return _.get(excusalCounts, "markedExcusals.totalCount", 0);

    default:
      return 0;
  }
};

export const getExcusalDate = (key, academicYearDate, dateType) => {
  const today = getTodaysDate();

  if (
    key === ATTENDANCE_CONSTANTS.UPCOMING_AND_ONGOING_EXCUSALS &&
    dateType === ATTENDANCE_CONSTANTS.START
  ) {
    return formatDate({
      date: today,
      format: ATTENDANCE_CONSTANTS.YYYY_MM_DD,
    });
  } else {
    return formatDate({
      date: academicYearDate,
      format: ATTENDANCE_CONSTANTS.YYYY_MM_DD,
    });
  }
};

export const getExcusalsEmptyStateSubTitle = ({
  t,
  key,
  selectedDatesObject,
}) => {
  switch (key) {
    case ATTENDANCE_CONSTANTS.NEW_EXCUSALS:
      return t("attendance:currently_no_new_excusals_to_display");
    case ATTENDANCE_CONSTANTS.UPCOMING_AND_ONGOING_EXCUSALS:
      return t(
        "attendance:currently_no_upcoming_or_ongoing_excusals_to_display"
      );
    case ATTENDANCE_CONSTANTS.MARKED_EXCUSALS: {
      const { startDate, endDate, dateViewType } = selectedDatesObject;
      const { dateLabel } = getFilterHeaderLabelsMemoize({
        startDate,
        endDate,
        dateViewType,
      });

      switch (dateViewType) {
        case ATTENDANCE_CONSTANTS.MONTHLY:
          return `${t(
            "attendance:no_marked_excusals_in_the_month_switch_to_yearly_view",
            { month: dateLabel }
          )} ${t(
            "attendance:switch_to_yearly_view_to_see_all_marked_excusals"
          )}`;
        case ATTENDANCE_CONSTANTS.WEEKLY:
          return `${t(
            "attendance:no_marked_excusals_in_the_duration_switch_to_yearly_view",
            { duration: dateLabel }
          )} ${t(
            "attendance:switch_to_yearly_view_to_see_all_marked_excusals"
          )}`;
        case ATTENDANCE_CONSTANTS.YEARLY:
          return t(
            "attendance:no_marked_excusals_in_the_duration_switch_to_yearly_view",
            { duration: dateLabel }
          );
      }
    }
  }
};

export const getAttendanceDetailsStripOptions = ({
  attendanceDetailType,
  t,
  periods,
  attendanceOverviewOnceADay,
  attendanceOptions,
  studentsAttendanceOverview,
  isAttendanceOnceADay,
  showHomeroomAttendance,
  attendanceCategories,
  isScheduledPeriodEnabled,
  isHomeroomClassesEnabled,
}) => {
  switch (attendanceDetailType) {
    case ATTENDANCE_CONSTANTS.CLASS_EACH_PERIOD:
      return _.reduce(
        periods,
        (periodsData, period) => {
          if (period.type === "REGULAR") {
            const periodStripData = {
              id: period.id,
              label: period.label,
              isDefault: isScheduledPeriodEnabled
                ? _.get(period, "isDefault", false)
                : false,
              totalCount: _.get(period, "selectedCourses.totalCount", 0),
              markedCount: _.get(
                period,
                "selectedCourses.statusWiseCourseAttendanceCount.markedCount",
                0
              ),
            };

            periodsData.push(periodStripData);
          }
          return periodsData;
        },
        []
      );

    case ATTENDANCE_CONSTANTS.CLASS_ONCE_A_DAY:
      if (getAttendanceHomeroomClassesEnabled()) {
        const DAILY_ATTENDANCE_WITH_CLASSES = getDailyAttendanceWithClasses({
          isHomeroomClassesEnabled,
        });

        return _.map(DAILY_ATTENDANCE_WITH_CLASSES, option => {
          return {
            id: _.get(option, "key"),
            subLabel: t(option.label),
            subLabelColor: _.get(option, "textColor"),
            optionCount: _.get(
              attendanceOverviewOnceADay,
              `${option.dataKey}`,
              0
            ),
            totalCount: _.get(
              attendanceOverviewOnceADay,
              "totalCoursesCount",
              0
            ),
          };
        });
      }
      return _.map(ATTENDANCE_DETAIL_HOMEROOM_ONCE_A_DAY, option => {
        return {
          id: option.key,
          subLabel: t(option.label),
          subLabelColor: option.textColor,
          optionCount: _.get(
            attendanceOverviewOnceADay,
            `${option.dataKey}.totalCount`,
            0
          ),
          totalCount: _.get(attendanceOverviewOnceADay, "staff.totalCount", 0),
        };
      });
    case "STUDENTS_TAB_OVERVIEW": {
      const ATTENDANCE_DETAIL_STUDENT_TAB_OVERVIEW = getAttendanceOverviewTabOptions(
        { studentsAttendanceOverview }
      );

      return _.map(ATTENDANCE_DETAIL_STUDENT_TAB_OVERVIEW, option => {
        const studentCount = getAttendanceTiersEnabled()
          ? _.get(option, "studentCount")
          : _.get(studentsAttendanceOverview, `${option.key}`, 0);
        return {
          id: option.key,
          subLabel: t(option.subLabel),
          subLabelColor: option.textColor,
          optionLabel: studentCount,
          optionSubLabel:
            option.key === ATTENDANCE_CONSTANTS.OVERALL_PRESENCE
              ? "%"
              : studentCount === 1
              ? t("common:student")
              : t("common:student_plural"),
          minPercentage: _.get(option, "minPercentage"),
          maxPercentage: _.get(option, "maxPercentage"),
          isAttendanceTier: _.get(option, "isAttendanceTier"),
        };
      });
    }
    case "STUDENTS_PERIOD_ATTENDANCE": {
      const markedBlock = {
        id: "MARKED",
        subLabel: t("attendance:students_marked"),
        subLabelColor: colors.black,
        optionCount: _.get(
          studentsAttendanceOverview,
          "totalStudentsMarked",
          0
        ),
        totalCount: _.get(studentsAttendanceOverview, "studentsCount", 0),
      };

      const anomalyBlock = {
        id: "ANOMALY",
        subLabel: t("attendance:with_an_anomaly"),
        subLabelColor: colors.black,
        optionCount: _.get(
          studentsAttendanceOverview,
          "anomalyStudentsCount",
          0
        ),
      };

      const attendanceSummary = _.get(
        studentsAttendanceOverview,
        "attendanceOptionsCount",
        []
      );

      const attendanceOptionsBlocks = getNewAttendanceDashboardsAndAnalyticsEnabled()
        ? _.map(attendanceCategories, ({ id, label, color }) => {
            const attendanceCategorySummaryDetail = _.find(
              attendanceSummary,
              ({ category }) => _.get(category, "id") === id
            );

            return {
              id: `${ATTENDANCE_CONSTANTS.ATTENDANCE_CATEGORY}_${id}`,
              subLabel: label,

              subLabelColor: getIsAttendanceCategoryColorEnabled()
                ? color
                : undefined,
              optionCount: _.get(attendanceCategorySummaryDetail, "count", 0),
            };
          })
        : _.map(attendanceOptions, ({ id, label, color }) => {
            const attendanceOptionSummaryDetail = _.find(
              attendanceSummary,
              ({ value }) => _.get(value, "id") === id
            );

            return {
              id: `${ATTENDANCE_CONSTANTS.ATTENDANCE_OPTION}_${id}`,
              subLabel: label,
              subLabelColor: color,
              optionCount: _.get(
                attendanceOptionSummaryDetail,
                "totalCount",
                0
              ),
            };
          });

      return isAttendanceOnceADay || showHomeroomAttendance
        ? [markedBlock, ...attendanceOptionsBlocks]
        : [markedBlock, anomalyBlock, ...attendanceOptionsBlocks];
    }
  }
};

export const getInitialSelectedDatesMemoize = _.memoize(
  params => getInitialSelectedDates(params),
  ({
    dateViewType,
    academicYearStartDate,
    noOfRotationalDay,
    rotationDays,
    isCurrentAcademicYear,
    academicYearEndDate,
    isActiveTabExcusals = false,
    attendanceRecordingFromDate,
  }) => {
    const currentDate = formatDate({ format: dateFormat });
    const baseKey = `${dateViewType}-${isCurrentAcademicYear}-${isActiveTabExcusals}-${attendanceRecordingFromDate}-${academicYearStartDate}-${academicYearEndDate}-${currentDate}`;

    if (dateViewType === "ROTATION_CYCLE") {
      const rotationDatesKey = _.join(
        _.map(rotationDays, d => _.get(d, "date")),
        ","
      );
      return `${baseKey}-${noOfRotationalDay}-${rotationDatesKey}`;
    }

    return baseKey;
  }
);

const getInitialSelectedDates = ({
  dateViewType,
  academicYearStartDate,
  noOfRotationalDay,
  rotationDays,
  isCurrentAcademicYear,
  academicYearEndDate,
  isActiveTabExcusals = false,
  attendanceRecordingFromDate,
}) => {
  const initialDate = !isCurrentAcademicYear ? academicYearEndDate : undefined;

  const currentDate = formatDate({ date: initialDate, format: dateFormat });
  const isAttendanceSettingsV2M2Enabled = getAttendanceSettingsV2M2Enabled();
  const isToddleV2AttendanceEnabled = checkIsToddleV2AttendanceEnabled();
  const attendanceRecordingFromDateExists =
    (isAttendanceSettingsV2M2Enabled || isToddleV2AttendanceEnabled) &&
    !_.isNil(attendanceRecordingFromDate);

  switch (dateViewType) {
    case ATTENDANCE_CONSTANTS.DAILY:
      return {
        startDate: null,
        endDate: currentDate,
      };
    case ATTENDANCE_CONSTANTS.WEEKLY: {
      const formattedStartDate = getFormattedStartDate({
        initialDate,
        format: dateFormat,
        startOfType: ATTENDANCE_CONSTANTS.WEEK,
      });
      return {
        startDate:
          attendanceRecordingFromDateExists &&
          !isActiveTabExcusals &&
          moment(moment(formattedStartDate)).isBefore(
            moment(attendanceRecordingFromDate)
          )
            ? formatDate({
                date: attendanceRecordingFromDate,
                format: dateFormat,
              })
            : formattedStartDate,
        endDate: getFormattedEndDate({
          initialDate,
          format: dateFormat,
          endOfType: ATTENDANCE_CONSTANTS.WEEK,
        }),
      };
    }
    case ATTENDANCE_CONSTANTS.MONTHLY: {
      const formattedStartDate = getFormattedStartDate({
        initialDate,
        format: dateFormat,
        startOfType: ATTENDANCE_CONSTANTS.MONTH,
      });
      return {
        startDate:
          attendanceRecordingFromDateExists &&
          !isActiveTabExcusals &&
          moment(moment(formattedStartDate)).isBefore(
            moment(attendanceRecordingFromDate)
          )
            ? formatDate({
                date: attendanceRecordingFromDate,
                format: dateFormat,
              })
            : formattedStartDate,
        endDate: isActiveTabExcusals
          ? getFormattedEndDate({
              initialDate,
              format: dateFormat,
              endOfType: ATTENDANCE_CONSTANTS.MONTH,
            })
          : currentDate,
      };
    }
    case ATTENDANCE_CONSTANTS.YEARLY:
      return {
        startDate: formatDate({
          date:
            attendanceRecordingFromDateExists &&
            !isActiveTabExcusals &&
            moment(moment(academicYearStartDate)).isBefore(
              moment(attendanceRecordingFromDate)
            )
              ? attendanceRecordingFromDate
              : academicYearStartDate,
          format: dateFormat,
        }),
        endDate: formatDate({
          date: academicYearEndDate,
          format: dateFormat,
        }),
      };
    case ATTENDANCE_GLOBAL_CONSTANTS.ROTATION_CYCLE: {
      const groupedRotationDays = groupRotationDaysMemoize({
        rotationDays,
        noOfDaysinRotationCycle: noOfRotationalDay,
      });

      const { index } = getRotationDayRangeMemoize({
        groupedRotationDays,
      });

      const selectedDateRangeObj = _.get(groupedRotationDays, `${index}`, {});

      return {
        startDate: selectedDateRangeObj?.start
          ? selectedDateRangeObj?.start
          : currentDate,
        endDate: selectedDateRangeObj?.end
          ? selectedDateRangeObj?.end
          : currentDate,
      };
    }
  }
};

export const generatePreviousDatesMemoize = _.memoize(
  params => generatePreviousDates(params),
  ({
    dateViewType,
    isActiveTabExcusals,
    attendanceRecordingFromDate,
    startDate,
    endDate,
    noOfRotationalDay,
    rotationDays,
  }) => {
    const baseKey = `${dateViewType}-${isActiveTabExcusals}-${attendanceRecordingFromDate}-${startDate}-${endDate}`;

    if (dateViewType === "ROTATION_CYCLE") {
      const rotationDatesKey = _.join(
        _.map(rotationDays, d => _.get(d, "date")),
        ","
      );
      return `${baseKey}-${noOfRotationalDay}-${rotationDatesKey}`;
    }

    return baseKey;
  }
);

const generatePreviousDates = ({
  dateViewType,
  isActiveTabExcusals,
  attendanceRecordingFromDate,
  startDate,
  endDate,
  noOfRotationalDay,
  rotationDays,
}) => {
  const isAttendanceSettingsV2M2Enabled = getAttendanceSettingsV2M2Enabled();
  const isToddleV2AttendanceEnabled = checkIsToddleV2AttendanceEnabled();
  const attendanceRecordingFromDateExists =
    (isAttendanceSettingsV2M2Enabled || isToddleV2AttendanceEnabled) &&
    !_.isNil(attendanceRecordingFromDate);
  const attendanceRecordingFromDateAsStartDate = moment(
    attendanceRecordingFromDate
  );
  const attendanceRecordingFromDateAsStartDateFormatted = attendanceRecordingFromDateAsStartDate.format(
    dateFormat
  );
  let newStartDate = moment(startDate).isBefore(
    moment(attendanceRecordingFromDate)
  )
    ? attendanceRecordingFromDate
    : startDate;
  let newEndDate = endDate;

  switch (dateViewType) {
    case "DAILY":
      newStartDate = null;
      newEndDate = moment(endDate).subtract(1, "day").format(dateFormat);
      break;
    case "WEEKLY": {
      const previousWeekDate = moment(startDate).subtract(1, "day");
      newStartDate =
        attendanceRecordingFromDateExists &&
        !isActiveTabExcusals &&
        moment(moment(previousWeekDate).startOf("week")).isBefore(
          attendanceRecordingFromDateAsStartDate
        )
          ? attendanceRecordingFromDateAsStartDateFormatted
          : moment(previousWeekDate).startOf("week").format(dateFormat);
      newEndDate = moment(previousWeekDate).endOf("week").format(dateFormat);
      break;
    }
    case "MONTHLY": {
      const previousWeekDate = moment(startDate).subtract(1, "day");
      newStartDate =
        attendanceRecordingFromDateExists &&
        !isActiveTabExcusals &&
        moment(moment(previousWeekDate).startOf("month")).isBefore(
          attendanceRecordingFromDateAsStartDate
        )
          ? attendanceRecordingFromDateAsStartDateFormatted
          : moment(previousWeekDate).startOf("month").format(dateFormat);
      newEndDate = moment(previousWeekDate).endOf("month").format(dateFormat);
      break;
    }
    case "ROTATION_CYCLE": {
      const groupedRotationDays = groupRotationDaysMemoize({
        rotationDays,
        noOfDaysinRotationCycle: noOfRotationalDay,
      });

      const findDateIndex = _.findIndex(groupedRotationDays, ({ start, end }) =>
        moment(startDate).isBetween(start, end, "date", "[]")
      );

      const selectedDateObj = _.get(
        groupedRotationDays,
        `${findDateIndex - 1}`,
        {}
      );

      newStartDate = selectedDateObj?.start
        ? attendanceRecordingFromDateExists &&
          !isActiveTabExcusals &&
          moment(moment(selectedDateObj?.start)).isBefore(
            attendanceRecordingFromDateAsStartDate
          )
          ? attendanceRecordingFromDateAsStartDateFormatted
          : selectedDateObj?.start
        : attendanceRecordingFromDateExists &&
          !isActiveTabExcusals &&
          moment(moment()).isBefore(attendanceRecordingFromDateAsStartDate)
        ? attendanceRecordingFromDateAsStartDateFormatted
        : moment().format("YYYY-MM-DD");
      newEndDate = selectedDateObj?.end
        ? selectedDateObj?.end
        : moment().format("YYYY-MM-DD");
      break;
    }
  }

  return {
    startDate: newStartDate,
    endDate: newEndDate,
  };
};

export const generateNextDatesMemoize = _.memoize(
  params => generateNextDates(params),
  ({
    dateViewType,
    startDate,
    endDate,
    noOfRotationalDay,
    rotationDays,
    isActiveTabExcusals = false,
  }) => {
    const baseKey = `${dateViewType}-${isActiveTabExcusals}-${startDate}-${endDate}`;

    if (dateViewType === "ROTATION_CYCLE") {
      const rotationDatesKey = _.join(
        _.map(rotationDays, d => _.get(d, "date")),
        ","
      );
      return `${baseKey}-${noOfRotationalDay}-${rotationDatesKey}`;
    }

    return baseKey;
  }
);

const generateNextDates = ({
  dateViewType,
  startDate,
  endDate,
  noOfRotationalDay,
  rotationDays,
  isActiveTabExcusals = false,
}) => {
  let newStartDate = startDate;
  let newEndDate = endDate;

  switch (dateViewType) {
    case "DAILY":
      newStartDate = null;
      newEndDate = moment(endDate).add(1, "day").format(dateFormat);
      break;
    case "WEEKLY": {
      const previousWeekDate = moment(endDate).add(1, "day");
      newStartDate = moment(previousWeekDate)
        .startOf("week")
        .format(dateFormat);

      newEndDate = moment(previousWeekDate).endOf("week").format(dateFormat);
      if (isActiveTabExcusals) {
        newEndDate = moment(previousWeekDate).endOf("week").format(dateFormat);
      }
      break;
    }
    case "MONTHLY": {
      const nextMonthDate = moment(endDate).add(1, "month");
      newStartDate = moment(nextMonthDate).startOf("month").format(dateFormat);
      const isAfter = moment(nextMonthDate)
        .endOf("month")
        .isAfter(moment(), "date");
      newEndDate = isAfter
        ? moment().format(dateFormat)
        : moment(nextMonthDate).endOf("month").format(dateFormat);
      if (isActiveTabExcusals) {
        newEndDate = moment(nextMonthDate).endOf("month").format(dateFormat);
      }
      break;
    }
    case "ROTATION_CYCLE": {
      const groupedRotationDays = groupRotationDaysMemoize({
        rotationDays,
        noOfDaysinRotationCycle: noOfRotationalDay,
      });

      const findDateIndex = _.findIndex(groupedRotationDays, ({ start, end }) =>
        moment(startDate).isBetween(start, end, "date", "[]")
      );

      const selectedDateObj = _.get(
        groupedRotationDays,
        `${findDateIndex + 1}`,
        {}
      );

      newStartDate = selectedDateObj?.start
        ? selectedDateObj?.start
        : moment().format("YYYY-MM-DD");
      newEndDate = selectedDateObj?.end
        ? selectedDateObj?.end
        : moment().format("YYYY-MM-DD");
      break;
    }
  }

  return {
    startDate: newStartDate,
    endDate: newEndDate,
  };
};

export const getAttendanceGlobalConstantsMemoize = _.memoize(
  params => getAttendanceGlobalConstants(params),
  ({ globalConstants, curriculumProgramId }) => {
    const constantNames = _.join(
      _.map(globalConstants, c => `${c.name}:${c.value}`),
      "-"
    );
    return `${curriculumProgramId}-${constantNames}`;
  }
);

const getAttendanceGlobalConstants = ({
  globalConstants,
  curriculumProgramId,
}) => {
  return _.reduce(
    globalConstants,
    (globalConstantsValue, globalConstant) => {
      const globalConstantType = _.get(globalConstant, "name", "");
      const globalConstantValue = _.get(globalConstant, "value");

      if (globalConstantType === "OPERATIONAL_DAYS") {
        globalConstantsValue["operationalWeekDays"] = globalConstantValue;
      } else if (globalConstantType === "ROUTINE_MODE") {
        globalConstantsValue["routineMode"] = globalConstantValue;
      } else if (
        globalConstantType === "ATTENDANCE_RECORDING_TYPE" &&
        _.get(globalConstant, "curriculumProgram.id", "") == curriculumProgramId
      ) {
        globalConstantsValue["attendanceRecordType"] = globalConstantValue;
      } else if (globalConstantType === "NO_OF_DAYS_IN_ROTATION_CYCLE") {
        globalConstantsValue["noOfRotationalDay"] = globalConstantValue;
      } else if (globalConstantType === "COUNT_HOLIDAYS_AS_ROTATION_DAY") {
        globalConstantsValue["countHolidayAsRotationDay"] = globalConstantValue;
      } else if (globalConstantType === "ATTENDANCE_AUTO_MARKING_TIME") {
        globalConstantsValue["attendanceAutoMarkingTime"] = globalConstantValue;
      } else if (globalConstantType === "ENABLE_ATTENDANCE_AUTO_MARKING") {
        globalConstantsValue[
          "isAttendanceAutoMarkingEnabled"
        ] = globalConstantValue;
      } else if (globalConstantType === "OVERALL_ATTENDANCE_PERCENTAGE_TYPE") {
        globalConstantsValue["attendancePercentageType"] = globalConstantValue;
      } else if (
        globalConstantType === "ATTENDANCE_OVERALL_PRESENCE_DECIMAL_NUMBER"
      ) {
        globalConstantsValue[
          "attendanceOverallPresenceDecimalNumber"
        ] = globalConstantValue;
      } else if (
        globalConstantType === "ATTENDANCE_OVERALL_PRESENCE_SCORE_CALCULATION"
      ) {
        globalConstantsValue[
          "attendanceOverallPresenceScoreCalculation"
        ] = globalConstantValue;
      } else if (
        globalConstantType === "HOMEROOM_PERIOD_ATTENDANCE_SYNC_TYPE"
      ) {
        globalConstantsValue["homeroomPeriodSyncType"] = globalConstantValue;
      } else if (globalConstantType === "ATTENDANCE_RECORDING_FROM_DATE") {
        globalConstantsValue[
          "attendanceRecordingFromDate"
        ] = globalConstantValue;
      } else if (globalConstantType === "DAILY_ATTENDANCE_RECORDING_TYPE") {
        globalConstantsValue["dailyRecordingMethod"] = globalConstantValue;
      } else if (
        globalConstantType === "OVERRIDE_LINK_PERIOD_FOR_BELL_SCHEDULE"
      ) {
        globalConstantsValue[
          "overrideLinkPeriodForBellSchedule"
        ] = globalConstantValue;
      }
      return globalConstantsValue;
    },
    {}
  );
};

export const getIsLinkPeriodByBellScheduleEnabled = () => {
  return ACLStore.can("FeatureFlag:LinkPeriodAtBellScheduleLevel");
};

export const getRotationDaysHashMapMemoize = _.memoize(
  params => getRotationDaysHashMap(params),
  params => {
    const { routinesByDate } = params;

    const mappedArray = _.map(routinesByDate, d => {
      const date = _.get(d, "date");
      const rotationDay = _.get(d, "rotationDay.label");
      return `${date}:${rotationDay}`;
    });

    const datesKey = _.join(mappedArray, "-");

    return `${datesKey}`;
  }
);

const getRotationDaysHashMap = ({ routinesByDate = [] }) => {
  return _.reduce(
    routinesByDate,
    (rotationDaysHashMap, rotationDayData) => {
      const date = _.get(rotationDayData, "date", "");
      const rotationDayNumber = _.get(
        rotationDayData,
        "rotationDay.label",
        null
      );

      rotationDaysHashMap[`${date}`] = rotationDayNumber;

      return rotationDaysHashMap;
    },
    {}
  );
};

export const getClassLabel = ({ coursesEdges, selectedId }) => {
  const selectedCourseEdge = _.find(
    coursesEdges,
    ({ node }) => node?.id === selectedId
  );

  return _.get(selectedCourseEdge, "node.title", "");
};

export const getFilterHeaderLabelsMemoize = _.memoize(
  params => getFilterHeaderLabels(params),
  ({
    startDate,
    endDate,
    rotationDaysHashMap,
    isRotationalCycle,
    dateViewType,
  }) => {
    const rotationDayKey = isRotationalCycle
      ? `${_.get(rotationDaysHashMap, startDate)}-${_.get(
          rotationDaysHashMap,
          endDate
        )}`
      : "";
    return `${dateViewType}-${startDate}-${endDate}-${isRotationalCycle}-${rotationDayKey}`;
  }
);

const getFilterHeaderLabels = ({
  startDate,
  endDate,
  rotationDaysHashMap,
  isRotationalCycle,
  dateViewType,
  t,
}) => {
  let dateLabel = "";
  let subDateLabel = "";

  switch (dateViewType) {
    case ATTENDANCE_CONSTANTS.DAILY: {
      dateLabel = moment(endDate).format("D MMMM YYYY");
      if (isRotationalCycle) {
        const date = moment(endDate).format("YYYY-MM-DD");

        const rotationDayNumber = _.get(rotationDaysHashMap, `${date}`);
        subDateLabel = rotationDayNumber
          ? t("common:rotation_day_text", {
              day: _.get(rotationDaysHashMap, `${date}`),
            })
          : "";
      }
      break;
    }
    case ATTENDANCE_CONSTANTS.WEEKLY:
    case ATTENDANCE_GLOBAL_CONSTANTS.ROTATION_CYCLE:
    case ATTENDANCE_CONSTANTS.CUSTOM_RANGE:
    case ATTENDANCE_CONSTANTS.YEARLY: {
      dateLabel = `${moment(startDate).format(
        DATE_TIME_CONSTANTS.D_MMM_YY
      )} - ${moment(endDate).format(DATE_TIME_CONSTANTS.D_MMM_YY)}`;
      if (isRotationalCycle && dateViewType !== ATTENDANCE_CONSTANTS.YEARLY) {
        const startDateRotationDay = _.get(rotationDaysHashMap, `${startDate}`);
        const endDateRotationDay = _.get(rotationDaysHashMap, `${endDate}`);

        if (startDateRotationDay && endDateRotationDay) {
          subDateLabel = `${t("common:rotation_day_text", {
            day: startDateRotationDay,
          })} - ${t("common:rotation_day_text", {
            day: endDateRotationDay,
          })}`;
        }
      }
      break;
    }
    case ATTENDANCE_CONSTANTS.MONTHLY: {
      dateLabel = moment(endDate).format(ATTENDANCE_CONSTANTS.MMMM_YYYY);
      break;
    }
  }
  return {
    dateLabel,
    subDateLabel,
  };
};

export const getPeriodsV3Data = ({ periods }) => {
  return _.map(_.get(periods, "edges", []), ({ node }) => node);
};

export const getRowValueMemoize = _.memoize(
  params => getRowValue(params),
  ({ rowType, rowData }) => {
    return `${rowType}-${JSON.stringify(rowData)}`;
  }
);

export const getPeriods = ({ rowData, isToddleV2TimetableEnabled }) => {
  switch (true) {
    case isToddleV2TimetableEnabled:
      return getPeriodsV3Data({ periods: _.get(rowData, "periodsV4", []) });
    default:
      return getPeriodsV3Data({ periods: _.get(rowData, "periodsV3", []) });
  }
};

const getTeachers = ({
  isAttendanceOnceADay,
  isAttendanceHomeroomClassesEnabled,
  activeTeachers,
  rnpEnabledTeacherIds,
  collaborators = [],
  isHomeroomClassesEnabled,
  isScheduledPeriodEnabled,
}) => {
  if (!isAttendanceHomeroomClassesEnabled) {
    return activeTeachers;
  }

  const isRnpConsumptionAllowed = getIsRnpConsumptionAllowed();

  if (!isRnpConsumptionAllowed) {
    return collaborators;
  }

  const shouldFilterByPermission =
    (isAttendanceOnceADay && !isScheduledPeriodEnabled) ||
    isHomeroomClassesEnabled;

  if (!shouldFilterByPermission) {
    return activeTeachers;
  }

  // If collaborators or rnpEnabledTeacherIds are empty, return an empty array
  if (_.isEmpty(collaborators) || _.isEmpty(rnpEnabledTeacherIds)) {
    return [];
  }

  // Only show collaborators who have attendance marking permission
  return _.filter(collaborators, collaborator =>
    _.includes(rnpEnabledTeacherIds, _.get(collaborator, "id"))
  );
};

const getRowValue = ({
  rowType,
  rowData,
  t,
  isAttendanceOnceADay,
  attendanceOptionsView,
  isActiveTabHomeroom,
  isScheduledPeriodEnabled,
  isHomeroomClassesEnabled,
  isToddleV2TimetableEnabled,
}) => {
  const isNewAttendanceDashboardsAndAnalyticsEnabled = getNewAttendanceDashboardsAndAnalyticsEnabled();

  switch (rowType) {
    case "class_name": {
      return isAttendanceOnceADay
        ? {
            label: _.get(rowData, "title", ""),
            courseId: _.get(rowData, "id", ""),
            labelColor: colors.textDefault,
          }
        : {
            label: _.get(rowData, "title", ""),
            courseId: _.get(rowData, "id", ""),
            scheduledPeriodId: _.get(
              rowData,
              "periodsV3.edges[0].node.period.id",
              ""
            ),
          };
    }
    case "status": {
      const attendanceMarkedStatus = _.get(
        rowData,
        "attendanceV2.attendanceMarkedStatus"
      );
      const { statusLabel, textColor } =
        _.find(
          ATTENDANCE_DETAIL_OPTION_ONCE_A_DAY({ t }),
          option => option.key === attendanceMarkedStatus
        ) || {};

      return {
        key: attendanceMarkedStatus,
        label: statusLabel || "-",
        labelColor: textColor,
        unmarkedStudents: _.get(rowData, "attendanceV2.unmarkedStudents", []),
      };
    }
    case "total_students":
      return {
        label: _.get(rowData, "allStudents.totalCount", 0),
      };
    case ATTENDANCE_CONSTANTS.ATTENDANCE_REMINDER: {
      return {
        attendanceReminderInfo: _.get(
          rowData,
          "attendanceV2.attendanceReminderInfo",
          []
        ),
        courseId: _.get(rowData, "id"),
        scheduledPeriodId: _.get(
          rowData,
          "periodsV3.edges[0].node.period.id",
          ""
        ),
      };
    }
    case "actions": {
      const periods = getPeriods({
        rowData,
        isToddleV2TimetableEnabled,
      });

      const selectedPeriod = _.first(periods);

      const allTeachers = isToddleV2TimetableEnabled
        ? _.map(_.get(selectedPeriod, "teachers", []), ({ staff }) => staff)
        : _.get(selectedPeriod, "teachers", []);

      const teachers = _.filter(allTeachers, teacher => {
        return !getUserArchivalStatus(teacher);
      });

      const teacherIds = _.map(teachers, ({ id }) => id);

      const courseGrades = _.get(rowData, "grades", []);
      const primaryGrade = _.get(rowData, "primaryGrade");

      return {
        teacherIds,
        assignedTeachers: teachers,
        courseGradeIds: _.map(courseGrades, ({ id }) => id),
        subject: _.get(selectedPeriod, "subject"),
        location: _.get(selectedPeriod, "location"),
        startTime: _.get(selectedPeriod, "startTime"),
        endTime: _.get(selectedPeriod, "endTime"),
        applicableTill: _.get(selectedPeriod, "applicableTill"),
        selectedClassId: _.get(rowData, "id", ""),
        selectedClassLabel: _.get(rowData, "title", ""),
        attendanceMarkedStatus: _.get(rowData, "attendanceMarkedStatus", ""),
        day: _.get(selectedPeriod, "day"),
        rotationId: _.get(selectedPeriod, "rotationDay.id", ""),
        timetableId: _.get(selectedPeriod, "timetable.id", ""),
        rotationEntityId: _.get(selectedPeriod, "rotationEntity.id", ""),
        primaryGrade,
        scheduledPeriodId: _.get(
          rowData,
          "periodsV3.edges[0].node.period.id",
          ""
        ),
      };
    }
    case "teacher_name": {
      const periods = getPeriods({
        rowData,
        isToddleV2TimetableEnabled,
      });

      const isAttendanceHomeroomClassesEnabled = getAttendanceHomeroomClassesEnabled();
      const attendanceData = _.head(
        _.get(rowData, "attendanceV2.edgeInfo.attendance", [])
      );

      const selectedPeriod = _.first(periods);

      const allTeachers = _.get(selectedPeriod, "teachers", []);

      const activeTeachers = _.filter(allTeachers, teacher => {
        return !getUserArchivalStatus(teacher);
      });

      const rnpEnabledTeacherIds = _.map(
        _.get(rowData, "enabledUsersV2.edges", []),
        ({ node }) => _.get(node, "id")
      );

      const collaborators = _.map(
        _.get(rowData, "collaborator.edges", []),
        ({ node: collaborator }) => collaborator
      );

      const teachers = getTeachers({
        isAttendanceOnceADay,
        isAttendanceHomeroomClassesEnabled,
        activeTeachers,
        rnpEnabledTeacherIds,
        collaborators,
        isHomeroomClassesEnabled,
        isScheduledPeriodEnabled,
      });

      const teachersCount = _.size(teachers);

      const firstTeacher = _.head(teachers);
      const teacherName =
        isAttendanceOnceADay && !isAttendanceHomeroomClassesEnabled
          ? _.get(attendanceData, "createdBy", "")
          : firstTeacher;

      const formattedTeacherName = getFormattedUserName({
        user: teacherName,
        type: "staff",
      });

      const teacherLabelOnceADay = formattedTeacherName
        ? formattedTeacherName
        : "-";

      const teacherLabelEachPeriod =
        teachersCount > 0
          ? teachersCount === 1
            ? formattedTeacherName
            : t("attendance:teacher_and_count_others", {
                teacher:
                  _.size(formattedTeacherName) <= 8
                    ? formattedTeacherName
                    : `${formattedTeacherName.substring(0, 5)}...`,
                count: teachersCount - 1,
              })
          : "-";

      const teacherLabel =
        isAttendanceOnceADay && !isAttendanceHomeroomClassesEnabled
          ? teacherLabelOnceADay
          : teacherLabelEachPeriod;

      const avatarDataEachPeriod = _.map(teachers, teacher => {
        return {
          id: _.get(teacher, "id"),
          imageUrl: _.get(teacher, "profileImage"),
          value: getFormattedUserName({
            user: teacher,
            type: "staff",
          }),
        };
      });

      const avatarDataOnceADay = _.size(attendanceData)
        ? [
            {
              id: _.get(attendanceData, "createdBy.id", ""),
              imageUrl: _.get(attendanceData, "createdBy.profileImage", ""),
              value: formattedTeacherName,
            },
          ]
        : [];

      return {
        avatarData:
          isAttendanceOnceADay && !isAttendanceHomeroomClassesEnabled
            ? avatarDataOnceADay
            : avatarDataEachPeriod,
        teacherLabel,
      };
    }
    default: {
      const isAttendanceHomeroomClassesEnabled = getAttendanceHomeroomClassesEnabled();
      const getStudentAttendanceSummary = () => {
        switch (attendanceOptionsView) {
          case ATTENDANCE_CONSTANTS.CODES: {
            return isAttendanceHomeroomClassesEnabled &&
              (isAttendanceOnceADay || isActiveTabHomeroom)
              ? _.get(rowData, "attendanceV2.categorySummary", [])
              : _.get(rowData, "attendanceV2.summary", []);
          }
          case ATTENDANCE_CONSTANTS.CATEGORIES: {
            return _.get(rowData, "attendanceV2.categorySummary", []);
          }
          case ATTENDANCE_CONSTANTS.CODES_AND_CATEGORIES: {
            return _.get(rowData, "attendanceV2.categorySummary", []);
          }
        }
      };

      const statusWiseSummary =
        isNewAttendanceDashboardsAndAnalyticsEnabled ||
        (isAttendanceHomeroomClassesEnabled &&
          (isAttendanceOnceADay || isActiveTabHomeroom))
          ? getStudentAttendanceSummary()
          : _.get(rowData, "attendanceV2.summary", []);

      const [attendanceCategoryId, key] = _.split(rowType, "-");

      const getStudentAttendanceStatistics = () => {
        switch (attendanceOptionsView) {
          case ATTENDANCE_CONSTANTS.CODES: {
            return isAttendanceHomeroomClassesEnabled &&
              (isAttendanceOnceADay || isActiveTabHomeroom)
              ? _.find(statusWiseSummary, ({ optionItems }) => {
                  return _.some(
                    optionItems,
                    ({ option }) => _.get(option, "id") === rowType
                  );
                })
              : _.find(
                  statusWiseSummary,
                  ({ value }) => _.get(value, "id") === rowType
                );
          }
          case ATTENDANCE_CONSTANTS.CATEGORIES: {
            return _.find(
              statusWiseSummary,
              ({ category }) => _.get(category, "id") === rowType
            );
          }
          case ATTENDANCE_CONSTANTS.CODES_AND_CATEGORIES: {
            return _.find(
              statusWiseSummary,
              ({ category }) => _.get(category, "id") === attendanceCategoryId
            );
          }
        }
      };

      const attendanceOptionObject =
        isNewAttendanceDashboardsAndAnalyticsEnabled ||
        (isAttendanceHomeroomClassesEnabled &&
          (isAttendanceOnceADay || isActiveTabHomeroom))
          ? getStudentAttendanceStatistics()
          : _.find(
              statusWiseSummary,
              ({ value, period }) =>
                _.get(value, "id") == rowType &&
                (isAttendanceOnceADay ? _.isNull(period) : true)
            );

      const getStudentAttendanceRecordCount = () => {
        switch (attendanceOptionsView) {
          case ATTENDANCE_CONSTANTS.CODES: {
            const attendanceOptionItems = _.get(
              attendanceOptionObject,
              "optionItems",
              []
            );

            const currentAttendanceOption = _.find(
              attendanceOptionItems,
              ({ option }) => {
                return _.get(option, "id") === rowType;
              }
            );

            return isAttendanceHomeroomClassesEnabled &&
              (isAttendanceOnceADay || isActiveTabHomeroom)
              ? _.get(currentAttendanceOption, "count", 0)
              : _.get(attendanceOptionObject, "totalCount", 0);
          }
          case ATTENDANCE_CONSTANTS.CATEGORIES: {
            return _.get(attendanceOptionObject, "count", 0);
          }
          case ATTENDANCE_CONSTANTS.CODES_AND_CATEGORIES: {
            if (key === "total") {
              return _.get(attendanceOptionObject, "count", 0);
            }
            const attendanceOptionItems = _.get(
              attendanceOptionObject,
              "optionItems"
            );
            return _.get(
              _.find(
                attendanceOptionItems,
                ({ option }) => _.get(option, "id") === key
              ),
              "count",
              0
            );
          }
        }
      };

      return {
        label:
          isNewAttendanceDashboardsAndAnalyticsEnabled ||
          (isAttendanceHomeroomClassesEnabled &&
            (isAttendanceOnceADay || isActiveTabHomeroom))
            ? getStudentAttendanceRecordCount()
            : _.size(statusWiseSummary)
            ? _.get(attendanceOptionObject, "totalCount", 0)
            : "-",
      };
    }
  }
};

export const getSelectedTabBasedLabels = ({ selectedTab, t }) => {
  switch (selectedTab) {
    case ATTENDANCE_CONSTANTS.STUDENTS:
      return {
        firstDropdownOptionsLabel: "common:classes_with_count",
        firstDropdownNoOptionSelectedLabel: t("common:no_class_plural"),
        firstDropdownAllOptionSelectedText: t("common:all_classes_plural"),
      };
    case ATTENDANCE_CONSTANTS.HOMEROOM_UPPERCASE:
    case ATTENDANCE_CONSTANTS.CLASSES:
    case ATTENDANCE_CONSTANTS.EXCUSALS:
      return {
        firstDropdownOptionsLabel: "onBoarding:grades_with_count",
        firstDropdownNoOptionSelectedLabel: t("common:no_grades"),
        firstDropdownAllOptionSelectedText: t("common:all_grades"),
      };
  }
};

export const checkIsActiveTabStudents = ({ activeTab }) => {
  return activeTab === ATTENDANCE_CONSTANTS.STUDENTS;
};

export const checkIsActiveTabClasses = ({ activeTab }) => {
  return activeTab === ATTENDANCE_CONSTANTS.CLASSES;
};

export const checkIsActiveTabHomeroom = ({ activeTab }) => {
  return activeTab === ATTENDANCE_CONSTANTS.HOMEROOM_UPPERCASE;
};

export const checkIsActiveTabExcusals = ({ activeTab }) => {
  return activeTab === ATTENDANCE_CONSTANTS.EXCUSALS;
};

export const checkIsRotationCycle = ({ routineMode }) => {
  return routineMode === ATTENDANCE_GLOBAL_CONSTANTS.ROTATION_CYCLE;
};

export const getEnrolledClassesOptions = ({ enrolledPeriods }) => {
  return _.map(enrolledPeriods, ({ course }) => {
    return {
      key: _.get(course, "id"),
      label: _.get(course, "title"),
    };
  });
};

export const getEnrolledPeriodsOptions = ({ enrolledPeriods }) => {
  return _.map(enrolledPeriods, ({ node }) => {
    return {
      key: _.get(node, "period.id"),
      label: _.get(node, "period.label"),
    };
  });
};

export const getTodaysEnrolledPeriods = ({
  selectedPeriods,
  isRotationalCycle,
  rotationDay,
  operationalDay,
  date,
}) => {
  return _.filter(
    selectedPeriods,
    ({
      rotationDay: rotationDayIdentity,
      day,
      isSelected,
      date: selectionDate,
    }) => {
      return isRotationalCycle
        ? moment(selectionDate).isSame(date, "day") &&
            rotationDayIdentity?.id === rotationDay?.id &&
            isSelected
        : moment(selectionDate).isSame(date, "day") &&
            day === operationalDay &&
            isSelected;
    }
  );
};

export const getSelectedOptionSendEmail = ({
  selectedAttendanceOption: optionId,
  attendanceOptions,
}) => {
  return _.find(attendanceOptions, ({ id, sendEmail }) => {
    return id === optionId && sendEmail;
  });
};

export const checkStudentIncluded = ({ studentFamilyIds, studentId }) => {
  return _.includes(studentFamilyIds, studentId);
};

export const getPeriodIndex = ({ selectedPeriods, periods }) => {
  return _.reduce(
    selectedPeriods,
    (periodsIndex, period, index) => {
      const isPeriodPresent = _.find(
        periods,
        ({ id }) => id === _.get(period, "id")
      );
      if (isPeriodPresent) {
        periodsIndex.push(index);
        return periodsIndex;
      }
      return periodsIndex;
    },
    []
  );
};

export const getUpdatedAttendanceDataMemoize = _.memoize(
  params => getUpdatedAttendanceData(params),
  ({ dateWiseAttendance, studentsAttendanceDetail, attendanceOptions }) => {
    return `${JSON.stringify(dateWiseAttendance)}-${JSON.stringify(
      studentsAttendanceDetail
    )}-${_.join(_.map(attendanceOptions, "id"), ",")}`;
  }
);

export const getUpdatedAttendanceData = ({
  dateWiseAttendance,
  studentsAttendanceDetail,
  attendanceOptions,
}) => {
  let sendSMS = false;

  const updatedStudentsAttendance = _.reduce(
    studentsAttendanceDetail,
    (updatedStudentsAttendance, attendanceDetail) => {
      const studentDetail = _.find(
        dateWiseAttendance,
        ({ student }) => student?.id === attendanceDetail.studentId
      );

      const dateWiseValue = _.head(_.get(studentDetail, "dateWiseValue", []));

      const attendanceOption = _.get(dateWiseValue, "value.id", null);
      const remark = _.get(dateWiseValue, "remark", "");

      if (
        (attendanceOption && remark != attendanceDetail.remark) ||
        attendanceDetail.attendanceOptionId !== attendanceOption
      ) {
        const selectedAttendanceOption = _.find(
          attendanceOptions,
          ({ id }) => id === attendanceDetail.attendanceOptionId
        );
        const sendSMSOption = _.get(selectedAttendanceOption, "sendSMS", false);

        if (sendSMSOption) {
          sendSMS = true;
        }

        updatedStudentsAttendance.push({
          studentId: attendanceDetail.studentId,
          remark: attendanceDetail.remark,
          optionId: attendanceDetail.attendanceOptionId,
          excusalId: attendanceDetail.excusalId,
        });
      }
      return updatedStudentsAttendance;
    },
    []
  );

  return {
    updatedStudentsAttendance,
    sendSMS,
  };
};

export const getGradesOptionsMemoize = _.memoize(
  params => getGradesOptions(params),
  ({ grades }) => {
    return _.join(_.map(grades, "id"), ",");
  }
);

const getGradesOptions = ({ grades }) => {
  return _.map(grades, ({ id, name }) => {
    return {
      value: id,
      label: name,
    };
  });
};

export const getEachPeriodWithDailyAttendancePeriodMemoize = _.memoize(
  params => getEachPeriodWithDailyAttendancePeriod(params),
  ({ periods, isPeriodByAttendance }) => {
    const periodIds = _.join(
      _.map(periods, period => _.get(period, "isDefault")),
      ","
    );
    return `${periodIds}-${isPeriodByAttendance}`;
  }
);

const getEachPeriodWithDailyAttendancePeriod = ({
  periods,
  isPeriodByAttendance,
}) => {
  return _.find(periods, period => {
    return isPeriodByAttendance && _.get(period, "isDefault", false);
  });
};

export const getDateViewTypeOptions = ({ t }) => {
  return [
    {
      key: ATTENDANCE_CONSTANTS.DAILY,
      label: t("common:daily"),
    },
    {
      key: ATTENDANCE_CONSTANTS.WEEKLY,
      label: t("common:weekly"),
    },
    {
      key: ATTENDANCE_CONSTANTS.MONTHLY,
      label: t("common:monthly"),
    },
    {
      key: ATTENDANCE_CONSTANTS.YEARLY,
      label: t("common:yearly"),
    },
    {
      key: ATTENDANCE_CONSTANTS.DIVIDER,
    },
    {
      key: ATTENDANCE_CONSTANTS.CUSTOM_RANGE,
      label: t("common:custom_range"),
    },
  ];
};

export const getExcusalsDateViewTypeOptions = ({ t }) => {
  return _.map(
    excusalsDateViewTypeOptionsMapping,
    option => {
      return { key: option.key, label: t(option.label) };
    },
    []
  );
};

export const getCurriculumPeriods = (periodDetails, curriculumProgramId) => {
  return _.find(
    periodDetails,
    ({ curriculumProgramId: curriculumId }) =>
      curriculumProgramId === curriculumId
  );
};

export const getFormattedDayNumberText = (startDay, endDay) => {
  const dayNumbers = [];
  for (let i = startDay + 1; i <= endDay; i++) {
    dayNumbers.push(i);
  }

  let formattedDayText = [];
  const dayNumbersSize = _.size(dayNumbers);
  for (let i = 0; i < dayNumbersSize; i++) {
    if (i === dayNumbersSize - 2) {
      formattedDayText = _.concat(formattedDayText, `${dayNumbers[i]} and `);
    } else if (i === dayNumbersSize - 1) {
      formattedDayText = _.concat(formattedDayText, `${dayNumbers[i]}`);
    } else {
      formattedDayText = _.concat(formattedDayText, `${dayNumbers[i]}, `);
    }
  }

  return _.join(formattedDayText, "");
};

export const checkPeriodAttendanceViewMemoize = _.memoize(
  params => checkPeriodAttendanceView(params),
  ({ isAttendanceOnceADay, dateViewType }) => {
    return `${isAttendanceOnceADay}-${dateViewType}`;
  }
);

const checkPeriodAttendanceView = ({ isAttendanceOnceADay, dateViewType }) => {
  return isAttendanceOnceADay
    ? _.includes(dateViewTypes, dateViewType)
    : dateViewType === "DAILY";
};

export const checkPeriodAttendanceHomeroomViewMemoize = _.memoize(
  params => checkPeriodAttendanceHomeroomView(params),
  ({ dateViewType }) => {
    return dateViewType;
  }
);

const checkPeriodAttendanceHomeroomView = ({ dateViewType }) => {
  return dateViewType === ATTENDANCE_CONSTANTS.DAILY;
};

export const getTrimedLabel = ({ label, noOfCharacter }) => {
  if (_.size(label) <= noOfCharacter) return label;
  return `${_.join(_.take(label, noOfCharacter), "")}...`;
};

export const getStudentAttendanceOverviewDataMemoize = _.memoize(
  params => getStudentAttendanceOverviewData(params),
  ({ queryDataNode, isPeriodAttendanceView }) => {
    return `${isPeriodAttendanceView}-${JSON.stringify(queryDataNode)}`;
  }
);

const getStudentAttendanceOverviewData = ({
  queryDataNode,
  isPeriodAttendanceView,
}) => {
  if (isPeriodAttendanceView) {
    const isNewAttendanceDashboardsAndAnalyticsEnabled = getNewAttendanceDashboardsAndAnalyticsEnabled();
    return {
      studentsCount: _.get(queryDataNode, "students.totalCount", 0),
      totalStudentsMarked: isNewAttendanceDashboardsAndAnalyticsEnabled
        ? _.get(queryDataNode, "attendanceV2.categorySummary.marked", 0)
        : _.get(queryDataNode, "attendanceV2.summary.marked", 0),
      anomalyStudentsCount: _.get(
        queryDataNode,
        "anomalyStudents.totalCount",
        0
      ),
      attendanceOptionsCount: isNewAttendanceDashboardsAndAnalyticsEnabled
        ? _.get(
            queryDataNode,
            "attendanceV2.categorySummary.attendanceCategoryCount",
            []
          )
        : _.get(queryDataNode, "attendanceV2.summary.attendanceCount", []),
    };
  }

  const annualAttendanceSummaryData = _.head(
    _.get(queryDataNode, "attendanceV2.percentageSummary", [])
  );
  const attendanceSummary = _.get(
    annualAttendanceSummaryData,
    "json_build_object",
    {}
  );

  const tierSummary = _.get(queryDataNode, "attendanceV2.tiersSummary", []);

  return {
    studentsCount: _.get(queryDataNode, "students.totalCount", 0),
    OVERALL_PRESENCE: getAttendanceTiersEnabled()
      ? _.get(tierSummary, "overallPresenceAvg")
      : _.get(attendanceSummary, "overall_presence", 0),
    PERFECT_ATTENDANCE: _.get(attendanceSummary, "100%", 0),
    MORE_THAN_90: _.get(attendanceSummary, "above_90", 0),
    LESS_THAN_90: _.get(attendanceSummary, "below_90", 0),
    tierSummary,
  };
};

export const getStudentEnrolledPeriodsMemoize = _.memoize(
  params => getStudentEnrolledPeriods(params),
  ({ rowType, enrolledPeriods }) => {
    return `${rowType}-${JSON.stringify(enrolledPeriods)}`;
  }
);

const getStudentEnrolledPeriods = ({ enrolledPeriods, rowType }) => {
  return _.reduce(
    enrolledPeriods,
    (studentEnrolledPeriods, { isSelected, period, course }) => {
      const isPeriodEnrolled = isSelected && _.get(period, "id") === rowType;

      if (isPeriodEnrolled) {
        studentEnrolledPeriods.push({
          key: _.get(course, "id"),
          label: _.get(course, "title"),
        });
      }
      return studentEnrolledPeriods;
    },
    []
  );
};

const getSelectedAttendanceOptionStudentRecord = ({
  studentAttendanceRecords,
  activeAttendanceOption,
}) => {
  const selectedAttendanceOptionRecord = _.find(
    studentAttendanceRecords,
    ({ node }) => node?.value?.id === activeAttendanceOption
  );

  return selectedAttendanceOptionRecord
    ? selectedAttendanceOptionRecord
    : _.head(studentAttendanceRecords);
};

export const getStudentTabRowValueMemoize = _.memoize(
  params => getStudentTabRowValue(params),
  ({ rowType, rowData, attendanceOptionsView, isOnceADayView }) => {
    return `${rowType}-${JSON.stringify(
      rowData
    )}-${attendanceOptionsView}-${isOnceADayView}`;
  }
);

const getEnrolledPeriods = ({ rowData, isToddleV2TimetableEnabled }) => {
  switch (true) {
    case isToddleV2TimetableEnabled:
      return _.get(rowData, "enrolledPeriodsV3", []);
    default:
      return _.get(rowData, "enrolledPeriodsV2", []);
  }
};

const getStudentTabRowValue = ({
  rowData,
  rowType,
  isAttendanceOnceADay,
  isPeriodAttendanceView,
  activeBlock,
  isPercentageAttendanceSummarySelected,
  isOnceADayView,
  isDailyAttendance,
  attendanceOptionsView,
  isToddleV2TimetableEnabled,
}) => {
  const isNewAttendanceDashboardsAndAnalyticsEnabled = getNewAttendanceDashboardsAndAnalyticsEnabled();
  const isAttendanceDataLoading = _.get(
    rowData,
    "isAttendanceDataLoading",
    false
  );
  const {
    id,
    profileImage,
    courses,
    familyMembers,
    alertTypes,
    attendanceV2,
    yearGroup,
  } = rowData;
  const isArchived = getUserArchivalStatus(rowData);
  switch (rowType) {
    case "student_name": {
      return {
        studentId: id,
        studentName: getFormattedUserName({
          user: rowData,
          showStudentUID: true,
          type: "student",
        }),
        profileImage,
        courses,
        isArchived,
        alertTypes,
        yearGroup,
      };
    }
    case CONSTANTS.PRESENCE: {
      const presenceValue =
        isPercentageAttendanceSummarySelected ||
        isOnceADayView ||
        !checkWeightedAttendanceCategoriesEnabled()
          ? "presencePercentage"
          : "presenceNumber";

      const presentPercentage = _.get(
        rowData,
        `attendanceV2.presenceOverview.${presenceValue}`,
        0
      );

      return {
        recordCount: presentPercentage,
        attendanceRecordId: rowType,
        recordCountColor: colors.textDefault,
        isAttendanceDataLoading,
      };
    }
    case CONSTANTS.ABSENCE: {
      const absenceValue = isPercentageAttendanceSummarySelected
        ? "absencePercentage"
        : "absenceNumber";

      const absencePercentage = _.get(
        rowData,
        `attendanceV2.presenceOverview.${absenceValue}`,
        0
      );

      return {
        recordCount: absencePercentage,
        attendanceRecordId: rowType,
        recordCountColor: colors.textDefault,
        isAttendanceDataLoading,
      };
    }
    case "TIERS": {
      return {
        ..._.get(attendanceV2, "presenceOverview.tier", {}),
        isAttendanceDataLoading,
      };
    }
    case CONSTANTS.TOTAL: {
      const totalMarkedNumber = _.get(
        rowData,
        `attendanceV2.presenceOverview.totalCount`,
        0
      );

      return {
        recordCount: totalMarkedNumber,
        attendanceRecordId: rowType,
        recordCountColor: colors.textDefault,
        isAttendanceDataLoading,
      };
    }
    case ATTENDANCE_CONSTANTS.ACTIONS: {
      return { studentId: _.get(rowData, "id", "") };
    }

    default: {
      const getStudentAttendanceSummary = () => {
        switch (attendanceOptionsView) {
          case ATTENDANCE_CONSTANTS.CODES: {
            return _.get(rowData, "attendanceV2.summary", []);
          }
          case ATTENDANCE_CONSTANTS.CATEGORIES:
          case ATTENDANCE_CONSTANTS.CODES_AND_CATEGORIES: {
            return _.get(
              rowData,
              "attendanceV2.categorySummary.countItems",
              []
            );
          }
        }
      };

      const getStudentStatistics = () => {
        switch (attendanceOptionsView) {
          case ATTENDANCE_CONSTANTS.CODES: {
            return _.get(rowData, "attendanceV2.statistics.categoryItems", []);
          }
          case ATTENDANCE_CONSTANTS.CATEGORIES: {
            return _.get(
              rowData,
              "attendanceV2.categorySummary.percentageItems",
              []
            );
          }
          case ATTENDANCE_CONSTANTS.CODES_AND_CATEGORIES: {
            return _.get(
              rowData,
              "attendanceV2.categorySummary.percentageItems",
              []
            );
          }
        }
      };
      const studentDateAttendance = _.get(rowData, "attendanceV2.edges", []);
      const studentAttendanceSummary = isNewAttendanceDashboardsAndAnalyticsEnabled
        ? getStudentAttendanceSummary()
        : _.get(rowData, "attendanceV2.summary", []);
      const studentAttendanceStatistics = isNewAttendanceDashboardsAndAnalyticsEnabled
        ? getStudentStatistics()
        : _.get(rowData, "attendanceV2.statistics.categoryItems", []);

      const [attendanceCategoryId, key] = _.split(rowType, "-");

      if (
        isPeriodAttendanceView &&
        !isAttendanceOnceADay &&
        !isDailyAttendance
      ) {
        const activeBlockKeys = _.split(activeBlock, "_");
        const isAttendanceOptionTabActive =
          _.first(activeBlockKeys) === ATTENDANCE_CONSTANTS.ATTENDANCE_OPTION;

        const studentAttendanceRecords = _.filter(
          studentDateAttendance,
          ({ node }) => node?.period?.id === rowType
        );

        const studentAttendanceRecordsCount = _.size(studentAttendanceRecords);

        const studentAttendanceRecord =
          studentAttendanceRecordsCount > 1 && isAttendanceOptionTabActive
            ? getSelectedAttendanceOptionStudentRecord({
                studentAttendanceRecords,
                activeAttendanceOption: _.last(activeBlockKeys),
              })
            : _.head(studentAttendanceRecords);

        const { firstName, lastName, profileImage } = rowData;

        const enrolledPeriods = getEnrolledPeriods({
          rowData,
          isToddleV2TimetableEnabled,
        });

        const selectedEnrolledPeriod = _.filter(enrolledPeriods, node => {
          return _.get(node, "period.id") === rowType;
        });

        const studentsEnrolledPeriod = getStudentEnrolledPeriodsMemoize({
          enrolledPeriods,
          rowType,
        });

        const studentName = getFormattedUserName({
          user: rowData,
          showStudentUID: true,
          type: "student",
        });

        const yearGroupGradeId = _.get(rowData, "yearGroup.grade.id", "");

        return {
          studentId: _.get(rowData, "id", ""),
          studentsEnrolledPeriod,
          firstName,
          lastName,
          studentName,
          profileImage,
          familyMembers,
          yearGroupGradeId,
          isAttendanceMarked: !_.isUndefined(studentAttendanceRecord),
          isRemarked: _.get(studentAttendanceRecord, "node.remark", ""),
          attendanceLabel: _.get(
            studentAttendanceRecord,
            "node.value.abbreviation"
          ),
          attendanceOptionLabel: _.get(
            studentAttendanceRecord,
            "node.value.label",
            ""
          ),
          studentCourses: _.get(rowData, "courses", []),
          selectedClassId:
            _.get(studentAttendanceRecord, "node.course.id", "") ||
            _.get(selectedEnrolledPeriod, "0.course.id", ""),
          color: getIsAttendanceCategoryColorEnabled()
            ? _.get(
                studentAttendanceRecord,
                "node.value.attendanceCategory.color"
              )
            : _.get(studentAttendanceRecord, "node.value.color"),
          attendanceRecordId: _.get(studentAttendanceRecord, "node.value.id"),
          isAttendanceDataLoading,
        };
      } else if (!isPeriodAttendanceView) {
        if (isPercentageAttendanceSummarySelected) {
          const getStudentAttendanceStatistics = () => {
            switch (attendanceOptionsView) {
              case ATTENDANCE_CONSTANTS.CODES: {
                return _.find(
                  studentAttendanceStatistics,
                  ({ item }) => _.get(item, "id") === rowType
                );
              }
              case ATTENDANCE_CONSTANTS.CATEGORIES: {
                return _.find(
                  studentAttendanceStatistics,
                  ({ category }) => _.get(category, "id") === rowType
                );
              }
              case ATTENDANCE_CONSTANTS.CODES_AND_CATEGORIES: {
                return _.find(
                  studentAttendanceStatistics,
                  ({ category }) =>
                    _.get(category, "id") === attendanceCategoryId
                );
              }
            }
          };

          const studentAttendanceRecord = isNewAttendanceDashboardsAndAnalyticsEnabled
            ? getStudentAttendanceStatistics()
            : _.find(
                studentAttendanceStatistics,
                ({ item }) => _.get(item, "id") === rowType
              );

          const getStudentAttendancePercentageRecord = () => {
            switch (attendanceOptionsView) {
              case ATTENDANCE_CONSTANTS.CODES: {
                return _.get(studentAttendanceRecord, "percentage", 0);
              }
              case ATTENDANCE_CONSTANTS.CATEGORIES: {
                return _.get(studentAttendanceRecord, "percentage", 0);
              }
              case ATTENDANCE_CONSTANTS.CODES_AND_CATEGORIES: {
                if (key === "total") {
                  return _.get(studentAttendanceRecord, "percentage", 0);
                }
                const attendanceOptionItems = _.get(
                  studentAttendanceRecord,
                  "optionItems"
                );
                return _.get(
                  _.find(
                    attendanceOptionItems,
                    ({ option }) => _.get(option, "id") === key
                  ),
                  "percentage",
                  0
                );
              }
            }
          };

          return {
            recordCount: isNewAttendanceDashboardsAndAnalyticsEnabled
              ? getStudentAttendancePercentageRecord()
              : _.get(studentAttendanceRecord, "percentage", 0),
            attendanceRecordId: _.get(studentAttendanceRecord, "item.id", ""),
            recordCountColor: colors.textDefault,
            isAttendanceDataLoading,
          };
        } else {
          const getStudentAttendanceRecord = () => {
            switch (attendanceOptionsView) {
              case ATTENDANCE_CONSTANTS.CODES: {
                return _.find(
                  studentAttendanceSummary,
                  ({ value }) => _.get(value, "id") === rowType
                );
              }
              case ATTENDANCE_CONSTANTS.CATEGORIES: {
                return _.find(
                  studentAttendanceSummary,
                  ({ category }) => _.get(category, "id") === rowType
                );
              }
              case ATTENDANCE_CONSTANTS.CODES_AND_CATEGORIES: {
                return _.find(
                  studentAttendanceSummary,
                  ({ category }) =>
                    _.get(category, "id") === attendanceCategoryId
                );
              }
            }
          };

          const studentAttendanceRecord = isNewAttendanceDashboardsAndAnalyticsEnabled
            ? getStudentAttendanceRecord()
            : _.find(
                studentAttendanceSummary,
                ({ value }) => _.get(value, "id") === rowType
              );

          const getStudentAttendanceRecordCount = () => {
            switch (attendanceOptionsView) {
              case ATTENDANCE_CONSTANTS.CODES: {
                return _.get(studentAttendanceRecord, "totalCount", 0);
              }
              case ATTENDANCE_CONSTANTS.CATEGORIES: {
                return _.get(studentAttendanceRecord, "count", 0);
              }
              case ATTENDANCE_CONSTANTS.CODES_AND_CATEGORIES: {
                if (key === "total") {
                  return _.get(studentAttendanceRecord, "count", 0);
                }
                const attendanceOptionItems = _.get(
                  studentAttendanceRecord,
                  "optionItems"
                );
                return _.get(
                  _.find(
                    attendanceOptionItems,
                    ({ option }) => _.get(option, "id") === key
                  ),
                  "count",
                  0
                );
              }
            }
          };

          return {
            recordCount: isNewAttendanceDashboardsAndAnalyticsEnabled
              ? getStudentAttendanceRecordCount()
              : _.get(studentAttendanceRecord, "totalCount", 0),
            attendanceRecordId: _.get(studentAttendanceRecord, "value.id", ""),
            recordCountColor: colors.textDefault,
            isAttendanceDataLoading,
          };
        }
      } else if (
        isPeriodAttendanceView &&
        (isAttendanceOnceADay || isDailyAttendance)
      ) {
        const studentAttendanceRecord = _.find(
          studentDateAttendance,
          ({ node }) =>
            ((checkIsDateSame({ date1: node?.date, date2: rowType }) ||
              rowType === ATTENDANCE_CONSTANTS.EXCUSAL) &&
              _.isNull(node?.period)) ||
            (isDailyAttendance && _.get(node, "isHomeroomAttendance"))
        );

        const { firstName, lastName, profileImage } = rowData;

        const attendanceClass = _.isUndefined(studentAttendanceRecord)
          ? _.get(_.head(courses), "id", "")
          : _.get(studentAttendanceRecord, "node.course.id", null);

        const studentName = getFormattedUserName({
          user: rowData,
          showStudentUID: true,
          type: "student",
        });

        const yearGroupGradeId = _.get(rowData, "yearGroup.grade.id", "");

        return {
          studentId: _.get(rowData, "id", ""),
          firstName,
          lastName,
          studentName,
          profileImage,
          familyMembers,
          yearGroupGradeId,
          isAttendanceMarked: !_.isUndefined(studentAttendanceRecord),
          isRemarked: _.get(studentAttendanceRecord, "node.remark", ""),
          attendanceLabel: _.get(
            studentAttendanceRecord,
            "node.value.abbreviation"
          ),
          attendanceOptionLabel: _.get(
            studentAttendanceRecord,
            "node.value.label",
            ""
          ),
          color: getIsAttendanceCategoryColorEnabled()
            ? _.get(
                studentAttendanceRecord,
                "node.value.attendanceCategory.color"
              )
            : _.get(studentAttendanceRecord, "node.value.color"),
          attendanceRecordId: _.get(studentAttendanceRecord, "node.value.id"),
          attendanceClass,
          studentCourses: _.get(rowData, "courses", []),
          isAttendanceDataLoading,
        };
      }
    }
  }
};

export const getSelectedStudentsCountMemoize = _.memoize(
  params => getSelectedStudentsCount(params),
  ({ studentCheckboxObj = {}, studentCount }) => {
    const {
      activeDropdownOption,
      checkedStudentIds,
      deSelectedStudentIds,
    } = studentCheckboxObj;

    return `${activeDropdownOption || ""}-${_.size(checkedStudentIds)}-${_.size(
      deSelectedStudentIds
    )}-${studentCount}`;
  }
);

const getSelectedStudentsCount = ({
  studentCheckboxObj = {},
  studentCount,
}) => {
  const {
    activeDropdownOption,
    checkedStudentIds,
    deSelectedStudentIds,
  } = studentCheckboxObj;

  const checkedStudentsCount = _.size(checkedStudentIds);
  const deSelectedStudentsCount = _.size(deSelectedStudentIds);

  if (_.isNull(activeDropdownOption)) {
    return checkedStudentsCount;
  }

  if (activeDropdownOption === ATTENDANCE_CONSTANTS.ALL) {
    return studentCount + checkedStudentsCount - deSelectedStudentsCount;
  }
};

const getCoursesCount = ({
  courseCount,
  checkedCoursesCount,
  deSelectedCoursesCount,
  notTeacherAssignedCourses = 0,
}) => {
  return (
    courseCount +
    checkedCoursesCount -
    deSelectedCoursesCount -
    notTeacherAssignedCourses
  );
};

export const getSelectedClassesCountMemoize = _.memoize(
  params => getSelectedClassesCount(params),
  ({
    sendReminderObj = {},
    onceADayClassesCount,
    selectedPeriodDetail,
    isAttendanceOnceADay,
    homeroomClassesCount: {
      totalCoursesCount,
      nonMarkedCoursesCount,
      partiallyMarkedCoursesCount,
    },
    isAttendanceHomeroomClassesEnabled,
    isActiveTabHomeroom,
  }) => {
    const {
      activeDropdownOption,
      checkedCourseIds,
      deSelectedCourseIds,
      notTeacherAssignedCourses = 0,
    } = sendReminderObj;

    let totalCount = _.get(
      selectedPeriodDetail,
      "selectedCourses.totalCount",
      0
    );

    if (isAttendanceHomeroomClassesEnabled && isActiveTabHomeroom) {
      totalCount = totalCoursesCount;
    } else if (isAttendanceOnceADay) {
      totalCount = _.get(onceADayClassesCount, "totalCount", 0);
    }

    return `${activeDropdownOption || ""}-${_.size(checkedCourseIds)}-${_.size(
      deSelectedCourseIds
    )}-${notTeacherAssignedCourses}-${totalCount}-${isAttendanceOnceADay}-${partiallyMarkedCoursesCount}-${nonMarkedCoursesCount}-${isActiveTabHomeroom}`;
  }
);

const getSelectedClassesCount = ({
  sendReminderObj = {},
  onceADayClassesCount,
  selectedPeriodDetail,
  isAttendanceOnceADay,
  homeroomClassesCount: {
    totalCoursesCount,
    nonMarkedCoursesCount,
    partiallyMarkedCoursesCount,
  },
  isAttendanceHomeroomClassesEnabled,
  isActiveTabHomeroom,
}) => {
  const {
    activeDropdownOption,
    checkedCourseIds,
    deSelectedCourseIds,
    notTeacherAssignedCourses = 0,
  } = sendReminderObj;

  const checkedCoursesCount = _.size(checkedCourseIds);
  const deSelectedCoursesCount = _.size(deSelectedCourseIds);

  if (_.isNull(activeDropdownOption)) {
    return checkedCoursesCount;
  }

  if (isAttendanceHomeroomClassesEnabled && isActiveTabHomeroom) {
    switch (activeDropdownOption) {
      case "ALL":
        return getCoursesCount({
          courseCount: totalCoursesCount,
          checkedCoursesCount,
          deSelectedCoursesCount,
          notTeacherAssignedCourses,
        });
      case "PARTIALLY_MARKED":
        return getCoursesCount({
          courseCount: partiallyMarkedCoursesCount,
          checkedCoursesCount,
          deSelectedCoursesCount,
          notTeacherAssignedCourses,
        });
      case "NOT_MARKED":
        return getCoursesCount({
          courseCount: nonMarkedCoursesCount,
          checkedCoursesCount,
          deSelectedCoursesCount,
          notTeacherAssignedCourses,
        });
    }
  }

  if (isAttendanceOnceADay) {
    switch (activeDropdownOption) {
      case "ALL":
        return getCoursesCount({
          courseCount: _.get(onceADayClassesCount, "totalCount", 0),
          checkedCoursesCount,
          deSelectedCoursesCount,
        });
      case "PARTIALLY_MARKED":
        return getCoursesCount({
          courseCount: _.get(
            onceADayClassesCount,
            "partiallyMarkedCoursesCount",
            0
          ),
          checkedCoursesCount,
          deSelectedCoursesCount,
        });
      case "NOT_MARKED":
        return getCoursesCount({
          courseCount: _.get(onceADayClassesCount, "notMarkedCoursesCount", 0),
          checkedCoursesCount,
          deSelectedCoursesCount,
        });
    }
  } else {
    switch (activeDropdownOption) {
      case "ALL":
        return getCoursesCount({
          courseCount: _.get(
            selectedPeriodDetail,
            "selectedCourses.totalCount",
            0
          ),
          checkedCoursesCount,
          deSelectedCoursesCount,
          notTeacherAssignedCourses,
        });
      case "PARTIALLY_MARKED":
        return getCoursesCount({
          courseCount: _.get(
            selectedPeriodDetail,
            "selectedCourses.statusWiseCourseAttendanceCount.partiallyMarkedCount",
            0
          ),
          checkedCoursesCount,
          deSelectedCoursesCount,
          notTeacherAssignedCourses,
        });
      case "NOT_MARKED":
        return getCoursesCount({
          courseCount: _.get(
            selectedPeriodDetail,
            "selectedCourses.statusWiseCourseAttendanceCount.notMarkedCount",
            0
          ),
          checkedCoursesCount,
          deSelectedCoursesCount,
          notTeacherAssignedCourses,
        });
    }
  }
};

export const getStudentsCount = ({
  studentsAttendanceOverview,
  activeBlock,
  isNewAttendanceDashboardsAndAnalyticsEnabled,
}) => {
  switch (activeBlock) {
    case "ANOMALY":
      return _.get(studentsAttendanceOverview, "anomalyStudentsCount", 0);
    case "PERFECT_ATTENDANCE":
    case "MORE_THAN_90":
    case "LESS_THAN_90":
      return _.get(studentsAttendanceOverview, `${activeBlock}`, 0);
    default: {
      if (
        getAttendanceTiersEnabled() &&
        !_.isEmpty(_.get(studentsAttendanceOverview, "tierSummary"))
      ) {
        const attendanceTiers = _.get(
          studentsAttendanceOverview,
          "tierSummary.tierSummary",
          []
        );

        const attendanceTier = _.find(attendanceTiers, ({ tier }) => {
          return _.get(tier, "id") === activeBlock;
        });

        return _.get(attendanceTier, "count", 0);
      }
      const attendanceOptionsCount = _.get(
        studentsAttendanceOverview,
        "attendanceOptionsCount",
        []
      );
      const attendanceOption = isNewAttendanceDashboardsAndAnalyticsEnabled
        ? _.find(
            attendanceOptionsCount,
            ({ category }) =>
              _.get(category, "id") === _.last(_.split(activeBlock, "_"))
          )
        : _.find(
            attendanceOptionsCount,
            ({ value }) =>
              _.get(value, "id") === _.last(_.split(activeBlock, "_"))
          );

      return isNewAttendanceDashboardsAndAnalyticsEnabled
        ? _.get(attendanceOption, "count", 0)
        : _.get(attendanceOption, "totalCount", 0);
    }
  }
};

export const getEmptyStateLabelMemoize = _.memoize(
  params => getEmptyStateLabel(params),
  ({ activeBlock, searchValue }) => {
    return `${activeBlock}-${searchValue}`;
  }
);

const getEmptyStateLabel = ({
  activeBlock,
  attendanceOptions,
  attendanceCategories,
  t,
  searchValue,
}) => {
  const commonSearchedNameEmptyStateLabel = t(
    "attendance:no_student_found_with_searched_name"
  );

  switch (activeBlock) {
    case ATTENDANCE_CONSTANTS.ANOMALY:
      return searchValue
        ? commonSearchedNameEmptyStateLabel
        : t("attendance:no_student_have_anomaly_yet");

    case ATTENDANCE_CONSTANTS.PERFECT_ATTENDANCE:
      return searchValue
        ? t("attendance:no_student_with_searched_name_perfect_attendance")
        : t("attendance:no_student_have_perfect_attendance");

    case ATTENDANCE_CONSTANTS.MORE_THAN_90:
      return searchValue
        ? t("attendance:no_student_with_searched_name_more_than_90_percent")
        : t("attendance:no_student_have_more_than_90_percent");

    case ATTENDANCE_CONSTANTS.LESS_THAN_90:
      return searchValue
        ? t("attendance:no_student_with_searched_name_less_than_90_percent")
        : t("attendance:no_student_have_less_than_90_percent");

    default: {
      const activeBlockKeys = _.split(activeBlock, "_");
      const isAttendanceOptionId =
        _.first(activeBlockKeys) === ATTENDANCE_CONSTANTS.ATTENDANCE_OPTION;

      const isAttendanceCategoryId =
        _.first(activeBlockKeys) === ATTENDANCE_CONSTANTS.ATTENDANCE_CATEGORY;

      if (isAttendanceOptionId || isAttendanceCategoryId) {
        const parsedAttendanceOptions = getNewAttendanceDashboardsAndAnalyticsEnabled()
          ? attendanceCategories
          : attendanceOptions;

        const selectedAttendanceOption = _.find(
          parsedAttendanceOptions,
          ({ id }) => id === _.last(activeBlockKeys)
        );

        return searchValue
          ? commonSearchedNameEmptyStateLabel
          : t("attendance:no_students_marked_with_category_yet", {
              category: _.get(selectedAttendanceOption, "label", ""),
              lowercase: true,
            });
      }
      return commonSearchedNameEmptyStateLabel;
    }
  }
};

export const checkWorkingDayMemoize = _.memoize(
  params => checkWorkingDay(params),
  ({
    dateViewType,
    date,
    operationalWeekDays,
    holidayEvents,
    isGradeBasedHoliday,
    selectedDate,
  }) => {
    return `${dateViewType}-${date}-${JSON.stringify(
      operationalWeekDays
    )}-${JSON.stringify(holidayEvents)}-${isGradeBasedHoliday}-${selectedDate}`;
  }
);

export const getHolidayEventGradesDatesLookup = ({ holidayEvents }) => {
  const isHoliday = {};

  _.forEach(holidayEvents, event => {
    const startDate = moment(event.start);
    const endDate = moment(event.end);

    _.forEach(_.range(0, endDate.diff(startDate, "days") + 1), idx => {
      const dateStr = startDate.clone().add(idx, "days").format("YYYY-MM-DD");

      _.set(isHoliday, [dateStr], _.get(isHoliday, dateStr, {}));

      _.forEach(event.associatedEntities, entity => {
        _.set(isHoliday, [dateStr, entity.id], true);
      });
    });
  });

  return isHoliday;
};

const checkWorkingDay = ({
  dateViewType,
  operationalWeekDays,
  date,
  holidayEvents,
  holidayEventGradesDatesLookup,
  grades,
  isGradeBasedHoliday = false,
}) => {
  const currentDay = moment(date).locale("en").format("ddd");
  const currentDayNumber = _.head(
    getDaysNameOrIndexArray({
      array: [currentDay],
      type: "index",
    })
  );

  const isOperationalDay = _.includes(operationalWeekDays, currentDayNumber);

  const gradesIds = _.map(grades, ({ id }) => id);

  const isHolidayForAllGrades = _.every(gradesIds, gradeId =>
    _.get(holidayEventGradesDatesLookup, [date, gradeId], false)
  );

  // Find the actual holiday event object to get the title
  const currentDateHolidayEvent = isGradeBasedHoliday
    ? isHolidayForAllGrades
      ? _.find(holidayEvents, ({ start, end }) =>
          moment(date).isBetween(start, end, "day", "[]")
        )
      : null
    : _.find(holidayEvents, ({ start, end }) =>
        moment(date).isBetween(start, end, "day", "[]")
      );

  return {
    isWorkingDay:
      dateViewType !== "DAILY" ||
      (isOperationalDay && !currentDateHolidayEvent),
    isOperationalDay,
    currentDateHolidayEvent,
  };
};

export const getSectionStudentsListMemoize = _.memoize(
  params => getSectionStudentsList(params),
  ({
    students,
    isHomeroom,
    dateWiseHomeroomPeriodAttendance,
    attendanceExcusalsConfiguration,
  }) => {
    return `${JSON.stringify(students)}-${isHomeroom}-${JSON.stringify(
      dateWiseHomeroomPeriodAttendance
    )}-${attendanceExcusalsConfiguration}`;
  }
);

const getSectionStudentsList = ({
  students,
  isDailyAttendance,
  fromHomeroomTab,
  isHomeroom,
  isAttendanceOnceADay,
  attendanceExcusalsConfiguration,
}) => {
  // Excusal visibility in sibling components (StudentAttendanceRow,
  // StudentsAttendanceDetailsTable) is still gated by checkAttendanceExcusalVisible()
  // (the feature flag). Aligning those to use attendanceExcusalsConfiguration is
  // tracked separately and intentionally out of scope for ATTD-504.
  if (!attendanceExcusalsConfiguration) {
    return {
      attendanceRecordStudents: students,
    };
  }

  return _.reduce(
    students,
    (sectionedStudentsList, student) => {
      const attendanceExcusals = _.get(
        student,
        "student.studentAttendanceExcusals.edges",
        []
      );
      const excusalsCount = _.size(attendanceExcusals);

      const isStudentEnrolled =
        !!_.size(_.get(student, "student.enrolledPeriodsV2")) ||
        !getAttendanceSettingsV2M2Enabled() ||
        isDailyAttendance ||
        isAttendanceOnceADay ||
        (!isHomeroom && !fromHomeroomTab);

      if (isStudentEnrolled) {
        if (excusalsCount > 0) {
          sectionedStudentsList["excusalStudents"] = sectionedStudentsList[
            "excusalStudents"
          ]
            ? [...sectionedStudentsList["excusalStudents"], student]
            : [student];
        } else {
          sectionedStudentsList[
            "attendanceRecordStudents"
          ] = sectionedStudentsList["attendanceRecordStudents"]
            ? [...sectionedStudentsList["attendanceRecordStudents"], student]
            : [student];
        }
      }

      return sectionedStudentsList;
    },
    {}
  );
};

export const getHomeroomRowValueMemoize = _.memoize(
  params => getHomeroomRowValue(params),
  ({ rowType, rowData }) => {
    return `${rowType}-${JSON.stringify(rowData)}`;
  }
);

const getHomeroomRowValue = ({
  rowType,
  rowData,
  t,
  attendanceOptionsView,
}) => {
  switch (rowType) {
    case ATTENDANCE_CONSTANTS.TEACHER_NAME: {
      const { id: teacherId, profileImage } = rowData || {};
      const teacherName = getFormattedUserName({
        user: rowData,
        type: "staff",
      });

      return {
        teacherName,
        teacherId,
        profileImage,
      };
    }
    case ATTENDANCE_CONSTANTS.STATUS: {
      const attendanceMarkedStatus = _.get(
        rowData,
        "homeroomAttendance.attendanceMarkedStatus"
      );
      const { statusLabel, textColor } =
        _.find(
          ATTENDANCE_DETAIL_OPTION_ONCE_A_DAY({ t }),
          option => option.key === attendanceMarkedStatus
        ) || {};

      return {
        key: attendanceMarkedStatus,
        label: statusLabel || "-",
        labelColor: textColor,
        unmarkedStudents: _.get(
          rowData,
          "homeroomAttendance.unmarkedStudents",
          []
        ),
      };
    }
    case ATTENDANCE_CONSTANTS.TOTAL_STUDENTS:
      return {
        label: _.get(rowData, "studentsRelation.totalCount", 0),
      };
    case ATTENDANCE_CONSTANTS.ATTENDANCE_REMINDER: {
      return {
        attendanceReminderInfo: _.get(
          rowData,
          "homeroomAttendance.attendanceReminderInfo",
          []
        ),
        staffId: _.get(rowData, "id"),
      };
    }
    case ATTENDANCE_CONSTANTS.ACTIONS: {
      const { firstName, lastName } = rowData || {};
      const teacherName = getFormattedUserName({
        user: rowData,
        type: "staff",
      });
      return {
        teacherId: _.get(rowData, "id"),
        teacherName,
        teacherFirstName: firstName,
      };
    }
    default: {
      const newAttendanceDashboardsAndAnalyticsEnabled = getNewAttendanceDashboardsAndAnalyticsEnabled();
      const [attendanceCategoryId, key] = _.split(rowType, "-");

      const getHomeroomAttendanceSummaryNode = () => {
        switch (attendanceOptionsView) {
          case ATTENDANCE_CONSTANTS.CODES: {
            return _.get(rowData, "homeroomAttendance.summary", []);
          }
          case ATTENDANCE_CONSTANTS.CATEGORIES: {
            return _.get(rowData, "homeroomAttendance.categorySummary", []);
          }
          case ATTENDANCE_CONSTANTS.CODES_AND_CATEGORIES: {
            return _.get(rowData, "homeroomAttendance.categorySummary", []);
          }
        }
      };

      const homeroomAttendanceSummary = newAttendanceDashboardsAndAnalyticsEnabled
        ? getHomeroomAttendanceSummaryNode()
        : _.get(rowData, "homeroomAttendance.summary", []);
      const getHomeroomAttendanceSummaryObj = () => {
        switch (attendanceOptionsView) {
          case ATTENDANCE_CONSTANTS.CODES: {
            return _.find(
              homeroomAttendanceSummary,
              ({ value }) => value?.id === rowType
            );
          }
          case ATTENDANCE_CONSTANTS.CATEGORIES: {
            return _.find(
              homeroomAttendanceSummary,
              ({ category }) => category?.id === rowType
            );
          }
          case ATTENDANCE_CONSTANTS.CODES_AND_CATEGORIES: {
            return _.find(
              homeroomAttendanceSummary,
              ({ category }) => category?.id === attendanceCategoryId
            );
          }
        }
      };

      const attendanceOptionObject = newAttendanceDashboardsAndAnalyticsEnabled
        ? getHomeroomAttendanceSummaryObj()
        : _.find(
            homeroomAttendanceSummary,
            ({ value }) => value?.id == rowType
          );

      const getHomeroomAttendanceRecordCount = () => {
        switch (attendanceOptionsView) {
          case ATTENDANCE_CONSTANTS.CODES: {
            return _.get(attendanceOptionObject, "totalCount", 0);
          }
          case ATTENDANCE_CONSTANTS.CATEGORIES: {
            return _.get(attendanceOptionObject, "count", 0);
          }
          case ATTENDANCE_CONSTANTS.CODES_AND_CATEGORIES: {
            if (key === "total") {
              return _.get(attendanceOptionObject, "count", 0);
            }
            const attendanceOptionItems = _.get(
              attendanceOptionObject,
              "optionItems"
            );
            return _.get(
              _.find(
                attendanceOptionItems,
                ({ option }) => _.get(option, "id") === key
              ),
              "count",
              0
            );
          }
        }
      };

      return {
        label: newAttendanceDashboardsAndAnalyticsEnabled
          ? getHomeroomAttendanceRecordCount()
          : !_.isEmpty(attendanceOptionObject)
          ? _.get(attendanceOptionObject, "totalCount", 0)
          : "-",
      };
    }
  }
};

export const getSelectedTeachersCountMemoize = _.memoize(
  params => getSelectedTeachersCount(params),
  ({ sendReminderObj = {}, homeroomAttendanceStaffOverview, activeBlock }) => {
    const {
      activeDropdownOption,
      checkedCourseIds,
      deSelectedCourseIds,
      notTeacherAssignedCourses = 0,
    } = sendReminderObj;

    const totalCount = _.get(
      homeroomAttendanceStaffOverview,
      "staff.totalCount",
      0
    );

    return `${activeDropdownOption || ""}-${_.size(checkedCourseIds)}-${_.size(
      deSelectedCourseIds
    )}-${notTeacherAssignedCourses}-${totalCount}-${activeBlock}`;
  }
);

const getSelectedTeachersCount = ({
  sendReminderObj = {},
  homeroomAttendanceStaffOverview,
  activeBlock,
}) => {
  const {
    activeDropdownOption,
    checkedCourseIds,
    deSelectedCourseIds,
    notTeacherAssignedCourses = 0,
  } = sendReminderObj;

  const checkedCoursesCount = _.size(checkedCourseIds);
  const deSelectedCoursesCount = _.size(deSelectedCourseIds);

  if (_.isNull(activeDropdownOption)) {
    return checkedCoursesCount;
  }

  switch (activeDropdownOption) {
    case ATTENDANCE_CONSTANTS.ALL: {
      const activeBlockAllSelection =
        activeBlock === ATTENDANCE_CONSTANTS.PARTIALLY_MARKED
          ? "partiallyMarkedAttendanceStaff"
          : activeBlock === ATTENDANCE_CONSTANTS.MARKED
          ? "staff"
          : "notMarkedAttendanceStaff";

      return (
        _.get(
          homeroomAttendanceStaffOverview,
          `${activeBlockAllSelection}.totalCount`,
          0
        ) +
        checkedCoursesCount -
        deSelectedCoursesCount -
        notTeacherAssignedCourses
      );
    }
    case ATTENDANCE_CONSTANTS.PARTIALLY_MARKED:
      return (
        _.get(
          homeroomAttendanceStaffOverview,
          "partiallyMarkedAttendanceStaff.totalCount",
          0
        ) +
        checkedCoursesCount -
        deSelectedCoursesCount -
        notTeacherAssignedCourses
      );
    case ATTENDANCE_CONSTANTS.NOT_MARKED:
      return (
        _.get(
          homeroomAttendanceStaffOverview,
          "notMarkedAttendanceStaff.totalCount",
          0
        ) +
        checkedCoursesCount -
        deSelectedCoursesCount -
        notTeacherAssignedCourses
      );
  }
};

export const getCoursesListMemoize = ({ courses }) => {
  return _.reduce(
    courses,
    (coursesList, course) => {
      const { id: value, title: label } = _.get(course, "node", {});

      const grades = _.get(course, "node.grades", []);
      const courseGradeList = _.map(grades, ({ id, name }) => {
        return {
          label,
          value,
          grade: {
            id,
            name,
          },
        };
      });

      return [...coursesList, ...courseGradeList];
    },
    []
  );
};

export const getDefaultPeriodLabel = periods => {
  const defaultPeriod = _.find(periods, ({ isDefault }) => isDefault);

  return {
    label: _.get(defaultPeriod, "label"),
    id: _.get(defaultPeriod, "id", null),
  };
};

export const getRandomizedAvatarColor = id => {
  const AVATAR_COLOR = ["neutral", "violet", "blue", "yellow", "pink", "teal"];

  return AVATAR_COLOR[id % 6];
};

export const getMaxOccurrenceValueFromArray = ({ array }) => {
  if (!_.size(array)) return null;

  const varMap = {};
  let maxValue = -99;
  let maxEl = null;
  _.forEach(array, data => {
    const value = varMap[data];

    if (!value) {
      varMap[data] = 1;
    } else {
      varMap[data]++;
    }

    if (varMap[data] > maxValue) {
      maxValue = varMap[data];
      maxEl = data;
    }
  });

  return maxEl;
};

const getAttendanceOptionsFilterVariables = ({
  organizationId,
  curriculumProgramId,
  currentAcademicYearId,
}) => {
  return {
    id: organizationId,
    selectedCurriculumId: curriculumProgramId,
    attendanceOptionsFilter: { academicYearId: currentAcademicYearId },
  };
};

export const getAttendanceOptionsFilterVariablesMemoize = _.memoize(
  params => getAttendanceOptionsFilterVariables(params),
  ({ organizationId, curriculumProgramId, currentAcademicYearId }) => {
    return `${organizationId}-${curriculumProgramId}-${currentAcademicYearId}`;
  }
);

const studentTabsOverViewFilters = ({
  organizationId,
  searchValue,
  searchTextFields,
  selectedClasses,
  curriculumProgramId,
  currentAcademicYearId,
  startDate,
  endDate,
  isAttendanceOnceADay,
  showHomeroomAttendance,
  isFromClass,
  isDailyView,
  isHomeroom,
  isPeriodAttendanceView,
  isAttendanceSettingsV2Enabled,
  isToddleV2AttendanceEnabled,
}) => {
  return {
    id: organizationId,
    studentsFilters: {
      searchText: searchValue,
      searchTextFields,
      courses: !isHomeroom ? selectedClasses : undefined,
      curriculumProgramIds: isHomeroom ? curriculumProgramId : undefined,
      academicYearId: currentAcademicYearId,
      academicYearIds: [currentAcademicYearId],
      enrollmentBeforeFilter: checkIsEnableEnrolWithdrawalDateEnabled()
        ? endDate
        : undefined,
    },
    anomalyStudentsFilters: {
      searchText: searchValue,
      searchTextFields,
      courses: selectedClasses,
      attendanceFilters: {
        dates: {
          startDate: startDate ? startDate : endDate,
          endDate,
        },
        showStudentAttendanceAnomaly: true,
        isPeriodByAttendance: !isAttendanceOnceADay,
      },
      academicYearId: currentAcademicYearId,
      academicYearIds: [currentAcademicYearId],
    },
    organizationAttendanceV2Filters: {
      startDate: startDate ? startDate : endDate,
      endDate,
      searchText: searchValue,
      courseIds: selectedClasses,
      isPeriodByAttendance: !isAttendanceOnceADay,
      academicYearIds: currentAcademicYearId,
      curriculumProgramIds: curriculumProgramId,
      onlyHomeroomAttendance:
        !isPeriodAttendanceView &&
        (isAttendanceSettingsV2Enabled || isToddleV2AttendanceEnabled)
          ? undefined
          : showHomeroomAttendance,
      filterAttendanceByCourseIds: isFromClass,
    },
    isPeriodAttendanceView: isDailyView,
    attendanceTiersFilters: {
      academicYearIds: [currentAcademicYearId],
      curriculumProgramIds: curriculumProgramId,
    },
  };
};

export const studentTabsOverViewFiltersMemoize = _.memoize(
  params => studentTabsOverViewFilters(params),
  ({
    organizationId,
    searchValue,
    selectedClasses,
    curriculumProgramId,
    currentAcademicYearId,
    startDate,
    endDate,
    isAttendanceOnceADay,
    showHomeroomAttendance,
    isFromClass,
    isDailyView,
    isHomeroom,
    isPeriodAttendanceView,
  }) => {
    const selectedClassesKey = _.join(selectedClasses, ",");
    return `${organizationId}-${searchValue}-${selectedClassesKey}-${curriculumProgramId}-${currentAcademicYearId}-${startDate}-${endDate}-${isAttendanceOnceADay}-${showHomeroomAttendance}-${isFromClass}-${isDailyView}-${isHomeroom}-${isPeriodAttendanceView}`;
  }
);

const getCurriculumProgramPeriodSetVariables = ({
  organizationId,
  curriculumProgramId,
  currentAcademicYearId,
}) => {
  return {
    id: organizationId,
    selectedCurriculumId: curriculumProgramId,
    periodSetFilter: {
      academicYearId: currentAcademicYearId,
    },
  };
};

export const getCurriculumProgramPeriodSetVariablesMemoize = _.memoize(
  params => getCurriculumProgramPeriodSetVariables(params),
  ({ organizationId, curriculumProgramId, currentAcademicYearId }) => {
    return `${organizationId}-${curriculumProgramId}-${currentAcademicYearId}`;
  }
);

const getSelectedPeriods = ({ periods }) => {
  return _.filter(periods, ({ type }) => type !== "BREAK");
};

export const getSelectedPeriodsMemoize = _.memoize(
  params => getSelectedPeriods(params),
  ({ periods }) => {
    return _.join(_.map(periods, "id"), ",");
  }
);

const getGlobalConstantsQueryFilter = ({ currentAcademicYearId, names }) => {
  return {
    academicYearIds: [currentAcademicYearId],
    names,
  };
};

export const getGlobalConstantsQueryFilterMemoize = _.memoize(
  params => getGlobalConstantsQueryFilter(params),
  ({ currentAcademicYearId, names }) => {
    const namesKey = _.join(names, ",");
    return `${currentAcademicYearId}-${namesKey}`;
  }
);

const getExculudedColumns = ({ userId, curriculumProgramId }) => {
  return getSettingValue({
    name: "AttendanceStudentTableColumns",
    userId,
    currentCurriculumProgramId: curriculumProgramId,
  });
};

export const getExculudedColumnsMemoize = _.memoize(
  params => getExculudedColumns(params),
  ({ userId, curriculumProgramId }) => {
    return `${userId}-${curriculumProgramId}`;
  }
);

const getHomeroomExcusalCountVariables = ({
  organizationId,
  curriculumProgramId,
  academicYearId,
  academicYearStartDate,
  academicYearEndDate,
}) => {
  const excusalFilters = {
    startDate: academicYearStartDate,
    endDate: academicYearEndDate,
    academicYearIds: academicYearId,
    curriculumProgramIds: curriculumProgramId,
    visibleTo: ATTENDANCE_CONSTANTS.HOMEROOM_UPPERCASE,
    attendanceExcusalStatus: ATTENDANCE_CONSTANTS.PENDING,
  };

  return {
    id: organizationId,
    newExcusalsFilters: excusalFilters,
    upcomingAndOngoingExcusalsFilters: {
      ...excusalFilters,
      startDate: formatDate({
        date: moment(),
        format: ATTENDANCE_CONSTANTS.YYYY_MM_DD,
      }),
      attendanceExcusalStatus: ATTENDANCE_CONSTANTS.MARKED,
    },
    markedExcusalsFilters: {
      ...excusalFilters,
      attendanceExcusalStatus: ATTENDANCE_CONSTANTS.MARKED,
    },
    studentsFilters: {
      academicYearIds: academicYearId,
      curriculumProgramIds: curriculumProgramId,
      visibleTo: ATTENDANCE_CONSTANTS.HOMEROOM_UPPERCASE,
    },
  };
};

export const getHomeroomExcusalCountVariablesMemoize = _.memoize(
  params => getHomeroomExcusalCountVariables(params),
  ({
    organizationId,
    curriculumProgramId,
    academicYearId,
    academicYearStartDate,
    academicYearEndDate,
  }) => {
    return `${organizationId}-${curriculumProgramId}-${academicYearId}-${academicYearStartDate}-${academicYearEndDate}`;
  }
);

const getOrganizationAttendanceExcusalCountFilter = ({
  allCourses,
  attendanceOptions,
  organizationId,
  academicYearId,
  academicYearStartDate,
  academicYearEndDate,
  curriculumProgramId,
}) => {
  const allCoursesId = _.map(allCourses, ({ node }) => node.id);

  const today = moment();
  const todayFormatted = today.format(ATTENDANCE_CONSTANTS.YYYY_MM_DD);

  const academicYearStartDateFormatted = formatDate({
    date: academicYearStartDate,
    format: ATTENDANCE_CONSTANTS.YYYY_MM_DD,
  });

  const academicYearEndDateFormatted = formatDate({
    date: academicYearEndDate,
    format: ATTENDANCE_CONSTANTS.YYYY_MM_DD,
  });

  const attendanceOptionIds = _.map(attendanceOptions, "id");

  return {
    id: organizationId,
    orderBy: ATTENDANCE_CONSTANTS.EXCUSAL_START_DATE,
    orderByDirection: ATTENDANCE_CONSTANTS.DESC,
    newExcusalsFilters: {
      startDate: academicYearStartDateFormatted,
      endDate: academicYearEndDateFormatted,
      attendanceExcusalStatus: ATTENDANCE_CONSTANTS.PENDING,
      curriculumProgramIds: curriculumProgramId,
      courseIds: allCoursesId,
      academicYearIds: academicYearId,
    },
    upcomingAndOngoingExcusalsFilters: {
      startDate: todayFormatted,
      endDate: academicYearEndDateFormatted,
      attendanceExcusalStatus: ATTENDANCE_CONSTANTS.MARKED,
      attendanceOptionIds,
      curriculumProgramIds: curriculumProgramId,
      courseIds: allCoursesId,
      academicYearIds: academicYearId,
    },
    markedExcusalsFilters: {
      startDate: academicYearStartDateFormatted,
      endDate: academicYearEndDateFormatted,
      attendanceExcusalStatus: ATTENDANCE_CONSTANTS.MARKED,
      attendanceOptionIds,
      curriculumProgramIds: curriculumProgramId,
      courseIds: allCoursesId,
      academicYearIds: academicYearId,
    },
  };
};

export const getOrganizationAttendanceExcusalCountFilterMemoize = _.memoize(
  params => getOrganizationAttendanceExcusalCountFilter(params),
  ({
    organizationId,
    academicYearId,
    academicYearStartDate,
    academicYearEndDate,
    curriculumProgramId,
  }) => {
    return `${organizationId}-${academicYearId}-${academicYearStartDate}-${academicYearEndDate}-${curriculumProgramId}`;
  }
);

export const getMemoizedCoursesFilters = _.memoize(
  value => value,
  JSON.stringify
);

const getGlobalConstantsQueryFilters = ({ currentAcademicYearId }) => {
  return {
    academicYearIds: [currentAcademicYearId],
    names: [ATTENDANCE_GLOBAL_CONSTANTS.OPERATIONAL_DAYS],
  };
};

export const getMemoizedGlobalConstantsQueryFilter = _.memoize(
  params => getGlobalConstantsQueryFilters(params),
  ({ currentAcademicYearId }) => {
    return currentAcademicYearId;
  }
);

const getCurriculumGlobalConstantsQueryFilter = ({
  currentAcademicYearId,
  curriculumProgramId,
}) => {
  return {
    academicYearIds: [currentAcademicYearId],
    curriculumProgramIds: [curriculumProgramId],
    names: [
      ATTENDANCE_GLOBAL_CONSTANTS.ROUTINE_MODE,
      ATTENDANCE_GLOBAL_CONSTANTS.ATTENDANCE_RECORDING_TYPE,
      ATTENDANCE_GLOBAL_CONSTANTS.NO_OF_DAYS_IN_ROTATION_CYCLE,
      ATTENDANCE_GLOBAL_CONSTANTS.ATTENDANCE_RECORDING_FROM_DATE,
      ATTENDANCE_GLOBAL_CONSTANTS.OVERALL_ATTENDANCE_PERCENTAGE_TYPE,
      ATTENDANCE_GLOBAL_CONSTANTS.HOMEROOM_PERIOD_ATTENDANCE_SYNC_TYPE,
      ATTENDANCE_GLOBAL_CONSTANTS.DAILY_ATTENDANCE_RECORDING_TYPE,
    ],
  };
};

export const getMemoizedCurriculumGlobalConstantsQueryFilter = _.memoize(
  params => getCurriculumGlobalConstantsQueryFilter(params),
  ({ currentAcademicYearId, curriculumProgramId }) => {
    return `${currentAcademicYearId}-${curriculumProgramId}`;
  }
);

const getAttendanceOptionsQueryFilterVariables = ({
  organizationId,
  curriculumProgramId,
  currentAcademicYearId,
}) => {
  return {
    id: organizationId,
    selectedCurriculumId: curriculumProgramId,
    attendanceOptionsFilter: { academicYearId: currentAcademicYearId },
  };
};

export const getMemoizedAttendanceOptionsFilterVariables = _.memoize(
  params => getAttendanceOptionsQueryFilterVariables(params),
  ({ organizationId, curriculumProgramId, currentAcademicYearId }) => {
    return `${organizationId}-${curriculumProgramId}-${currentAcademicYearId}`;
  }
);

const getCurriculumDefaultPeriodFilters = ({
  organizationId,
  curriculumProgramId,
  currentAcademicYearId,
}) => {
  return {
    id: organizationId,
    curriculumIds: curriculumProgramId,
    periodSetFilters: {
      academicYearId: currentAcademicYearId,
    },
    periodFilters: {
      onlyDefaultPeriods: true,
    },
  };
};

export const getMemoizedCurriculumDefaultPeriodFilters = _.memoize(
  params => getCurriculumDefaultPeriodFilters(params),
  ({ organizationId, curriculumProgramId, currentAcademicYearId }) => {
    return `${organizationId}-${curriculumProgramId}-${currentAcademicYearId}`;
  }
);

const getClassesAttendanceDetailsOverviewEachPeriodVariables = ({
  organizationId,
  curriculumProgramId,
  currentAcademicYearId,
  selectedDate,
  isScheduledPeriodEnabled,
  isAttendanceOnceADay,
  todayGradesWithoutHoliday,
}) => {
  return {
    id: organizationId,
    curriculumIds: curriculumProgramId,
    periodSetFilters: {
      academicYearId: currentAcademicYearId,
    },
    selectedCoursesFilters: {
      date: selectedDate,
      curriculumProgramIds: curriculumProgramId,
      academicYearId: currentAcademicYearId,
      onlyHomeroomAttendance: isScheduledPeriodEnabled && isAttendanceOnceADay,
      primaryGrades: todayGradesWithoutHoliday,
    },
  };
};

export const getMemoizedClassesAttendanceDetailsOverviewEachPeriodVariables = _.memoize(
  params => getClassesAttendanceDetailsOverviewEachPeriodVariables(params),
  ({
    organizationId,
    curriculumProgramId,
    currentAcademicYearId,
    selectedDate,
    isActiveTabHomeroom,
    recordingMethod,
    todayGradesWithoutHoliday,
  }) => {
    return `${organizationId}-${curriculumProgramId}-${currentAcademicYearId}-${selectedDate}-${isActiveTabHomeroom}-${recordingMethod}`;
  }
);

const getClassesAttendanceDetailsOverviewOnceADayVariables = ({
  organizationId,
  curriculumProgramId,
  currentAcademicYearId,
  routineMode,
  selectedDate,
}) => {
  return {
    id: organizationId,
    statusWiseCountFilters: {
      courseAttendanceFilter: {
        routineMode,
        date: selectedDate,
        curriculumProgramIds: curriculumProgramId,
      },
      curriculumProgramIds: curriculumProgramId,
      academicYears: currentAcademicYearId,
    },
  };
};

export const getMemoizedClassesAttendanceDetailsOverviewOnceADayVariables = _.memoize(
  params => getClassesAttendanceDetailsOverviewOnceADayVariables(params),
  ({
    organizationId,
    curriculumProgramId,
    currentAcademicYearId,
    routineMode,
    selectedDate,
  }) => {
    return `${organizationId}-${curriculumProgramId}-${currentAcademicYearId}-${routineMode}-${selectedDate}`;
  }
);

export const getAttendanceDetailsForEachPeriodFiltersMemoize = _.memoize(
  params => getAttendanceDetailsForEachPeriodFilters(params),
  ({
    organizationId,
    sortEntity,
    direction,
    selectedDate,
    isAttendanceOnceADay,
    activeBlock,
    curriculumProgramId,
    currentAcademicYearId,
    searchValue,
    selectedGrades,
    recordingMethod,
    defaultPeriodId,
    isHomeroomClassesEnabled,
    isActiveTabHomeroom,
    isToddleV2TimetableEnabled,
    isLinkPeriodByBellScheduleEnabled,
    isRnpConsumptionAllowed,
    todayGradesWithoutHoliday,
  }) => {
    return `${organizationId}-${sortEntity}-${direction}-${selectedDate}-${isAttendanceOnceADay}-${activeBlock}-${curriculumProgramId}-${currentAcademicYearId}-${searchValue}-${selectedGrades}-${recordingMethod}-${defaultPeriodId}-${isHomeroomClassesEnabled}-${isActiveTabHomeroom}-${isToddleV2TimetableEnabled}-${isLinkPeriodByBellScheduleEnabled}-${isRnpConsumptionAllowed}-${todayGradesWithoutHoliday}`;
  }
);

export const getAttendanceDetailsForEachPeriodFilters = ({
  organizationId,
  sortEntity,
  direction,
  selectedDate,
  isAttendanceOnceADay,
  activeBlock,
  curriculumProgramId,
  currentAcademicYearId,
  searchValue,
  selectedGrades,
  recordingMethod,
  defaultPeriodId,
  isHomeroomClassesEnabled,
  isActiveTabHomeroom,
  isToddleV2TimetableEnabled,
  isLinkPeriodByBellScheduleEnabled,
  isRnpConsumptionAllowed,
  todayGradesWithoutHoliday,
}) => {
  const isAttendanceHomeroomClassesEnabled = getAttendanceHomeroomClassesEnabled();
  const parsedPeriodId =
    isAttendanceOnceADay ||
    isHomeroomClassesEnabled ||
    isActiveTabHomeroom ||
    activeBlock === "ALL"
      ? undefined
      : activeBlock;
  const isScheduledPeriodEnabled =
    isAttendanceHomeroomClassesEnabled &&
    (isAttendanceOnceADay || isActiveTabHomeroom) &&
    recordingMethod === "SCHEDULE_PERIOD" &&
    defaultPeriodId;

  return {
    id: organizationId,
    first: 20,
    orderBy: sortEntity,
    orderByDirection: direction,
    academicYearId: currentAcademicYearId,
    coursesFilters: {
      courseAttendanceFilter: {
        date: selectedDate,
        courseAttendanceStatus:
          (isAttendanceHomeroomClassesEnabled || isScheduledPeriodEnabled) &&
          (isActiveTabHomeroom || isAttendanceOnceADay)
            ? activeBlock
            : "ALL",
        periodIds:
          isScheduledPeriodEnabled && !isLinkPeriodByBellScheduleEnabled
            ? defaultPeriodId
            : parsedPeriodId,
        curriculumProgramIds: curriculumProgramId,
        academicYearId: currentAcademicYearId,
        onlyHomeroomAttendance: isAttendanceHomeroomClassesEnabled
          ? isAttendanceOnceADay || isHomeroomClassesEnabled
          : undefined,
        filterByLinkPeriods:
          recordingMethod === "SCHEDULE_PERIOD" &&
          (isActiveTabHomeroom || isAttendanceOnceADay)
            ? true
            : undefined,
      },
      primaryGrades: todayGradesWithoutHoliday,
      academicYears: currentAcademicYearId,
      curriculumProgramIds: curriculumProgramId,
      searchText: searchValue,
      isHomeroom:
        recordingMethod === "HOMEROOM_CLASS" &&
        (isActiveTabHomeroom || isAttendanceOnceADay),
    },
    periodsFilters: {
      startDate: selectedDate,
      endDate: selectedDate,
      periodIds:
        isScheduledPeriodEnabled && !isAttendanceOnceADay && isActiveTabHomeroom
          ? undefined
          : parsedPeriodId,
      curriculumProgramIds: curriculumProgramId,
      isAllDayPeriods: false,
      academicYearId: currentAcademicYearId,
      filterByLinkPeriods:
        recordingMethod === "SCHEDULE_PERIOD" &&
        (isActiveTabHomeroom || isAttendanceOnceADay)
          ? true
          : undefined,
    },
    courseAttendanceV2Filters: {
      dates: {
        startDate: selectedDate,
        endDate: selectedDate,
      },
      periodIds:
        isScheduledPeriodEnabled &&
        !isAttendanceOnceADay &&
        !isLinkPeriodByBellScheduleEnabled
          ? defaultPeriodId
          : parsedPeriodId,
      academicYearIds: [currentAcademicYearId],
      onlyHomeroomAttendance: isAttendanceHomeroomClassesEnabled
        ? isAttendanceOnceADay || isHomeroomClassesEnabled
        : undefined,
    },
    skipStaffList:
      !isAttendanceHomeroomClassesEnabled || !isRnpConsumptionAllowed,
    staffFilters: {
      controlParameterId: "170721751980835037",
      allowContextBuildInternally: true,
      context: [
        { entityId: curriculumProgramId, entityType: "CURRICULUM_PROGRAM" },
      ],
    },
    collaboratorFilters: {
      academicYearIds: [currentAcademicYearId],
    },
    isToddleV2TimetableEnabled,
  };
};

// this function generate holiday events lookup
// it keeps date as key and value as true if holiday event is present on that date
export const createHolidayEventsLookUp = ({ holidayEvents }) => {
  return _.reduce(
    holidayEvents,
    (result, event) => {
      let { start: startDate } = event;
      const { end: endDate } = event;

      while (moment(startDate).isSameOrBefore(endDate)) {
        result[moment(startDate).format("YYYY-MM-DD")] = true;
        startDate = moment(startDate).add(1, "days");
      }
      return result;
    },
    {}
  );
};

export const getActiveTabFromSearchParams = tab => {
  switch (tab) {
    case "excusals":
      return ATTENDANCE_CONSTANTS.EXCUSALS;
    case "students":
      return ATTENDANCE_CONSTANTS.STUDENTS;
    case "classes":
      return ATTENDANCE_CONSTANTS.CLASSES;
    case "homerooms":
      return ATTENDANCE_CONSTANTS.HOMEROOM_UPPERCASE;
    default:
      return ATTENDANCE_CONSTANTS.CLASSES;
  }
};

export const getSearchParamsFormActiveTab = tab => {
  switch (tab) {
    case ATTENDANCE_CONSTANTS.STUDENTS:
      return "students";
    case ATTENDANCE_CONSTANTS.CLASSES:
      return "classes";
    case ATTENDANCE_CONSTANTS.HOMEROOM_UPPERCASE:
      return "homerooms";
    case ATTENDANCE_CONSTANTS.EXCUSALS:
      return "excusals";
    default:
      return "classes";
  }
};

export const checkPushBackAttendanceToSISEnabled = () =>
  ACLStore.can("FeatureFlag:PushBackAttendanceToSIS");

export const checkIsEnableEnrolWithdrawalDateEnabled = () =>
  ACLStore.can("FeatureFlag:EnableEnrolWithdrawalDate");

export const getReportOptions = ({
  t,
  isFromClass,
  isHomeroomEnabled,
  isAttendanceOnceADay,
  attendanceRecordType,
  curriculumDefaultPeriod,
  viewHomeroomAttendancePermission,
}) => {
  const isEachPeriodWithDailyAttendance =
    (!isAttendanceOnceADay && !_.isEmpty(curriculumDefaultPeriod)) ||
    ((getAttendanceSettingsV2M2Enabled() ||
      checkIsToddleV2AttendanceEnabled()) &&
      attendanceRecordType === "EACH_PERIOD_WITH_ONCE_A_DAY");

  const showHomeroomReports =
    isHomeroomEnabled && viewHomeroomAttendancePermission;

  const studentReports = [
    {
      label: t("attendance:individual_attendance_reports"),
      key: "STUDENT_REPORTS",
      options: [
        {
          label: t("attendance:student_summary_report"),
          key: "STUDENT_SUMMARY_REPORTS",
        },
        ...(!(
          getAttendanceSettingsV2Enabled() || checkIsToddleV2AttendanceEnabled()
        ) &&
        !isAttendanceOnceADay &&
        !_.isEmpty(curriculumDefaultPeriod) &&
        showHomeroomReports
          ? [
              {
                label: t("attendance:student_summary_report_homeroom"),
                key: "STUDENT_SUMMARY_REPORT_HOMEROOM",
              },
            ]
          : []),
        ...(showHomeroomReports &&
        (((getAttendanceSettingsV2Enabled() ||
          checkIsToddleV2AttendanceEnabled()) &&
          isEachPeriodWithDailyAttendance) ||
          (!(
            getAttendanceSettingsV2Enabled() ||
            checkIsToddleV2AttendanceEnabled()
          ) &&
            !isAttendanceOnceADay &&
            !_.isEmpty(curriculumDefaultPeriod)))
          ? [
              {
                label: t("attendance:student_attendance_records_homeroom"),
                key: "STUDENT_ATTENDANCE_RECORDS_HOMEROOM",
              },
            ]
          : []),
        ...(isAttendanceOnceADay || isFromClass
          ? [
              {
                label: t("attendance:student_attendance_records"),
                key: "STUDENT_ATTENDANCE_RECORDS",
              },
            ]
          : []),
        {
          label: t("attendance:individual_student_reports"),
          key: "INDIVIDUAL_STUDENT_REPORTS",
        },
      ],
    },
  ];

  if (isFromClass) {
    return studentReports;
  }

  const classReports =
    !isEachPeriodWithDailyAttendance &&
    ((isAttendanceOnceADay && !isHomeroomEnabled) || !isAttendanceOnceADay)
      ? [
          {
            label: t("attendance:class_reports"),
            key: "CLASS_REPORTS",
            options: [
              {
                label: t("attendance:class_summary_report"),
                key: "CLASS_SUMMARY_REPORT",
              },
              ...(!isAttendanceOnceADay
                ? [
                    {
                      label: t("attendance:classwise_attendance_records"),
                      key: "CLASSWISE_ATTENDANCE_RECORDS",
                    },
                  ]
                : []),
            ],
          },
        ]
      : [];

  const homeroomReports =
    isAttendanceOnceADay && showHomeroomReports
      ? [
          {
            label: t("attendance:homeroom_reports"),
            key: "SUMMARY_REPORTS",
            options: [
              {
                label: t("attendance:homeroom_summary_report"),
                key: "HOMEROOM_SUMMARY_REPORT",
              },
              {
                label: t("attendance:homeroomwise_attendance_records"),
                key: "HOMEROOMWISE_ATTENDANCE_RECORDS",
              },
            ],
          },
        ]
      : [];

  const summaryAndRecordReports = isEachPeriodWithDailyAttendance
    ? [
        {
          label: t("attendance:attendance_records"),
          key: "ATTENDANCE_RECORDS",
          options: [
            {
              label: t("attendance:classwise_attendance_records"),
              key: "CLASSWISE_ATTENDANCE_RECORDS",
            },
            ...(showHomeroomReports
              ? [
                  {
                    label: t("attendance:homeroomwise_attendance_records"),
                    key: "HOMEROOMWISE_ATTENDANCE_RECORDS",
                  },
                ]
              : []),
          ],
        },
        {
          label: t("attendance:summary_reports"),
          key: "SUMMARY_REPORTS",
          options: [
            {
              label: t("attendance:class_summary_report"),
              key: "CLASS_SUMMARY_REPORT",
            },
            ...(showHomeroomReports
              ? [
                  {
                    label: t("attendance:homeroom_summary_report"),
                    key: "HOMEROOM_SUMMARY_REPORT",
                  },
                ]
              : []),
          ],
        },
      ]
    : [];

  return [
    ...studentReports,
    ...summaryAndRecordReports,
    ...homeroomReports,
    ...classReports,
  ];
};

export const getSegmentedProgressByStep = ({ step }) => {
  const step1Color = _.includes(["STEP_1", "STEP_2", "STEP_3"], step)
    ? colors.surfaceDark
    : colors.neutral600;
  const step2Color = _.includes(["STEP_2", "STEP_3"], step)
    ? colors.surfaceDark
    : colors.neutral600;
  const step3Color = _.includes(["STEP_3"], step)
    ? colors.surfaceDark
    : colors.neutral600;

  return [
    {
      id: "STEP_1",
      color: step1Color,
      progress: 33,
    },
    {
      id: "GAP_1",
      color: "#ffffff",
      progress: 0.5,
    },
    {
      id: "STEP_2",
      color: step2Color,
      progress: 33,
    },
    {
      id: "GAP_2",
      color: "#ffffff",
      progress: 0.5,
    },
    {
      id: "STEP_3",
      color: step3Color,
      progress: 33,
    },
  ];
};

export const getSelectDurationModalInfoText = ({ type }) => {
  // TODO: localize text
  switch (type) {
    case "CLASS_SUMMARY_REPORT":
      return t("attendance:step2_class_summary_description");
    case "HOMEROOM_SUMMARY_REPORT":
      return t("attendance:step2_homeroom_summary_description");
    case "CLASSWISE_ATTENDANCE_RECORDS":
      return t("attendance:step2_class_summary_description");
    case "HOMEROOMWISE_ATTENDANCE_RECORDS":
      return t("attendance:step2_homeroom_wise_attendance_description");
    case "STUDENT_SUMMARY_REPORTS":
      return t("attendance:step2_student_summary_description");
    case "STUDENT_ATTENDANCE_RECORDS":
      return t("attendance:step2_student_attendance_description");
    case "INDIVIDUAL_STUDENT_REPORTS":
      return t("attendance:step2_individual_student_description");
    case "STUDENT_CLASS_WISE_ATTENDANCE_COUNT_SUMMARY":
      return "Download a summary report of student class-wise attendance count for the selected date range.";
    case "STUDENTS_HOMEROOM_ATTENDANCE_REPORT_SUMMARY":
      return "Download a summary report of student homeroom attendance for the selected date range.";
  }
};

export const getSelectEntityModalSubtitleText = ({ type }) => {
  // TODO: localize text
  let entityType, modalInfoText;

  switch (type) {
    case "CLASS_SUMMARY_REPORT":
    case "CLASSWISE_ATTENDANCE_RECORDS": {
      entityType = t("common:classes");
      modalInfoText = t("attendance:step3_class_description");
      break;
    }
    case "HOMEROOM_SUMMARY_REPORT":
    case "HOMEROOMWISE_ATTENDANCE_RECORDS": {
      entityType = t("common:lowercase", {
        text: t("attendance:homerooms"),
      });
      modalInfoText = t("attendance:step3_homeroom_description");
      break;
    }
    case "STUDENT_SUMMARY_REPORTS":
    case "STUDENT_ATTENDANCE_RECORDS":
    case "INDIVIDUAL_STUDENT_REPORTS": {
      entityType = t("common:students");
      modalInfoText = t("attendance:step3_students_description");
      break;
    }
  }

  return {
    modalSubtitle: t("attendance:step3_select_label", {
      label: entityType,
    }),
    modalInfoText,
  };
};

export const formattedStudentDetail = ({
  id,
  fullName,
  profileImage,
  yearGroupName,
  alertTypes,
}) => {
  return {
    icon: (
      <Avatar
        name={fullName}
        src={profileImage}
        size="medium"
        shape="square"
        shade="light"
        color={getRandomizedAvatarColor(id)}
      />
    ),
    key: id,
    label: fullName,
    subText: yearGroupName,
    alertTypes,
  };
};

const groupedStudentDropdownOptions = ({
  studentsList,
  getFormattedStudentName,
  t,
  moreClassesStyle,
  defaultStudentNameOrderPreference,
  avatarShape = "circle",
}) => {
  const gradeIdNameMapping = new Map();
  const groupedStudents = new Map();

  _.forEach(studentsList, student => {
    const { id: gradeId, name: gradeName } = _.get(
      student,
      "node.allGrades[0]",
      {}
    );

    if (!groupedStudents.has(gradeId)) {
      groupedStudents.set(gradeId, []);
      gradeIdNameMapping.set(gradeId, gradeName);
    }

    groupedStudents.get(gradeId).push(student);
  });

  const gradeIds = [...groupedStudents.keys()];

  return _.map(gradeIds, grade => {
    const students = groupedStudents.get(grade);
    const options = _.map(students, ({ node }) => {
      const { id, profileImage, alertTypes } = node;
      const yearGroupName = _.get(node, "yearGroup.name", "");
      const fullName = getFormattedStudentName({
        student: node,
        isFormattedNameV2: true,
        defaultStudentNameOrderPreference,
      });
      return formattedStudentDetail({
        id,
        fullName,
        profileImage,
        yearGroupName,
        alertTypes,
      });
    });
    return {
      key: grade,
      label: gradeIdNameMapping.get(grade),
      isItemGroup: true,
      options,
    };
  });
};

export const groupedStudentDropdownOptionsMemoized = _.memoize(
  params => groupedStudentDropdownOptions(params),
  ({ studentsList, defaultStudentNameOrderPreference, avatarShape }) => {
    const studentIds = _.join(
      _.map(studentsList, student => _.get(student, "node.id", "")),
      ","
    );
    return `${studentIds}-${defaultStudentNameOrderPreference}-${avatarShape}`;
  }
);

export const hasArchivedStudentsV2InList = data => {
  const totalEntities = _.size(
    _.filter(data, item => {
      const isArchived =
        getUserArchivalStatus(_.get(item, "student")) ||
        getUserArchivalStatus(_.get(item, "node"));
      return isArchived;
    })
  );
  return totalEntities > 0;
};

export const getParsedTableColumns = ({
  t,
  tableColumns,
  renderHeaderColumn,
  attendanceRecordingType,
  columnHeader,
}) => {
  if (!_.isEqual(attendanceRecordingType, "EACH_PERIOD_WITH_ONCE_A_DAY")) {
    return tableColumns;
  }

  const dailyColumn = {
    alignment: "center",
    key: "DAILY_ATTENDANCE",
    uiType: "attendance",
    value: renderHeaderColumn({
      isDefaultPeriod: false,
      label: columnHeader,
      value: columnHeader,
      t,
    }),
  };

  tableColumns = _.concat(
    _.slice(tableColumns, 0, 1),
    [dailyColumn],
    _.slice(tableColumns, 1)
  );

  return tableColumns;
};

export const getAttendanceDropdownOptions = ({ t, attendanceCategories }) => {
  const columns = [
    {
      key: ATTENDANCE_CONSTANTS.CATEGORIES,
      label: t("common:category_plural"),
      isEnabled: true,
    },
    {
      key: ATTENDANCE_CONSTANTS.CODES,
      label: t("common:code_other"),
      isEnabled: true,
    },
    {
      key: ATTENDANCE_CONSTANTS.CODES_AND_CATEGORIES,
      label: t("attendance:code_and_categories"),
      isEnabled: _.some(
        attendanceCategories,
        attendanceCategory =>
          _.size(_.get(attendanceCategory, "attendanceOptions")) > 1
      ),
    },
  ];
  return _.filter(columns, ({ isEnabled }) => isEnabled);
};

export const getAttendanceDashboardPermissionVariables = ({
  curriculumProgramId,
}) => {
  return {
    filters: {
      module: "ATTENDANCE_DASHBOARD",
      appType: ["TEACHER_WEB", "ADMIN_WEB"],
      contexts: [
        [
          {
            entityId: curriculumProgramId,
            entityType: "CURRICULUM_PROGRAM",
          },
        ],
      ],
    },
  };
};

export const getHomeroomVariables = ({ curriculumProgramId }) => {
  return {
    filters: {
      module: "HOMEROOM",
      appType: ["TEACHER_WEB", "ADMIN_WEB"],
      contexts: [
        [
          {
            entityId: curriculumProgramId,
            entityType: "CURRICULUM_PROGRAM",
          },
        ],
      ],
    },
  };
};

export const getIsRnpConsumptionAllowed = () => {
  return ACLStore.can(ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION);
};

export const getPermissionsValue = ({ permissions }) => {
  return _.map(permissions, permission =>
    ACLStorage.getPermissionValue(permission)
  );
};

export const getPermissionsValueByContext = ({ permissions, context }) => {
  return _.map(permissions, permission =>
    ACLStorage.getPermissionValueByContext(permission, context)
  );
};

const attendanceRnpPermissions = [
  ATTENDANCE_DASHBOARD_ACCESS_PERMISSION,
  HOMEROOM_ACCESS_PERMISSION,
  ATTENDANCE_SETTINGS_ACCESS_PERMISSION,
  ATTENDANCE_SETTINGS_V2_ACCESS_PERMISSION,
];

const filterAttendancePermissions = controlParameters => {
  return controlParameters
    ?.filter(controlParam =>
      _.includes(attendanceRnpPermissions, controlParam.permission)
    )
    ?.map(controlParam => controlParam.id);
};

export const getAttendancePermissionIds = rnpPermissions => {
  const permissionIds = [];
  rnpPermissions.aclControlParameters.forEach(permission => {
    const attendancePermissionId = filterAttendancePermissions(
      permission.controlParameters
    );
    permissionIds.push(...attendancePermissionId);
  });

  return permissionIds;
};

export const getStudentsPresentInCampusReportDownloadDropdownOptions = () => {
  const dropdownOptions = [
    {
      key: ATTENDANCE_CONSTANTS.STUDENT_CAMPUS_PRESENCE_GRADE_SUMMARY_REPORT,
      label: "Students not in campus",
    },
  ];

  return dropdownOptions;
};

export const checkIsDownloadPresenceReportEnabled = () => {
  return ACLStore.can("FeatureFlag:DownloadPresentInCampusAttendanceReport");
};

export const checkIsStudentBulkAttendanceMarkingEnabled = () => {
  return ACLStore.can("FeatureFlag:StudentBulkAttendanceMarking");
};

export const checkIsRnpConsumptionEnabled = () => {
  return ACLStore.can(ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION);
};

export const getClassTabBulkActionToolbarOptions = ({ t }) => [
  {
    key: ATTENDANCE_CONSTANTS.MARK_ATTENDANCE,
    label: t("attendance:mark_attendance"),
    icon: <StudentEditOutlined />,
    showLabel: true,
    disabled: false,
  },
  {
    key: ATTENDANCE_CONSTANTS.SEND_REMINDER,
    label: t("attendance:send_reminder"),
    icon: <NotificationBellOutlined />,
    showLabel: true,
    disabled: false,
  },
];

export const getStudentsTabBulkActionToolbarOptions = ({
  t,
  markAttendancePermission,
  manageExcusalsPermission,
}) => {
  const options = [
    {
      key: ATTENDANCE_CONSTANTS.MARK_ATTENDANCE,
      label: t("attendance:mark_attendance"),
      icon: <StudentEditOutlined />,
      showLabel: true,
      isVisible: markAttendancePermission,
    },
    {
      key: ATTENDANCE_CONSTANTS.RAISE_EXCUSAL,
      label: "Raise excusal",
      icon: <EnvelopePlainOutlined />,
      showLabel: true,
      isVisible: manageExcusalsPermission,
    },
  ];
  return _.filter(options, option => option.isVisible);
};

export const getSortedStudentAttendanceRecords = ({
  recordData,
  markAttendanceSortOption,
}) => {
  const orderBy = _.get(markAttendanceSortOption, "orderBy");
  const orderByDirection = _.get(markAttendanceSortOption, "orderByDirection");

  if (orderBy === ATTENDANCE_CONSTANTS.ATTENDANCE_COUNT) {
    return _.orderBy(
      recordData,
      [
        record =>
          _.get(record, "student.attendanceV2.edges.length", 0) > 0 ? 1 : 0,
      ],
      [orderByDirection === ATTENDANCE_CONSTANTS.ASC ? "asc" : "desc"]
    );
  }

  return recordData;
};

const getColumnWidthForDataGrid = ({ columnKey, isStudentTabExists }) => {
  // not included NOTES column as its width is calculated dynamically based on the presence of other columns
  const columnWidths = {
    STUDENTS: "24%",
    DATE: "12%",
    PERIOD: "12%",
    CLASS: isStudentTabExists ? "12%" : "18%",
    ATTENDANCE: "12%",
    EXCUSAL: "7%",
  };

  return columnWidths[columnKey] || "auto";
};

export const getBulkMarkAttendanceGridColumns = ({
  t,
  columnOrder,
  isAttendanceOnceADay,
  isDailyAttendance,
  isHomeroomClassTab = false,
}) => {
  const isStudentTabExists = _.includes(columnOrder, "STUDENTS");

  // calculate notes column width
  const totalColumnWidthWithoutNotes = _.reduce(
    columnOrder,
    (totalWidth, columnKey) => {
      if (columnKey === "NOTES") {
        return totalWidth;
      }
      const columnWidth = getColumnWidthForDataGrid({
        columnKey,
        isStudentTabExists,
      });
      if (!columnWidth) {
        return totalWidth;
      }
      const parsedColumnWidth = parseInt(columnWidth, 10);
      if (_.isNaN(parsedColumnWidth)) {
        return totalWidth;
      }
      return totalWidth + parsedColumnWidth;
    },
    0
  );
  const notesColumnWidth = 100 - totalColumnWidthWithoutNotes;

  const headerColumns = _.map(columnOrder, columnKey => {
    const columnWidth = getColumnWidthForDataGrid({
      columnKey,
      isStudentTabExists,
    });
    switch (columnKey) {
      case "STUDENTS":
        return {
          id: "STUDENTS",
          columnKey: "STUDENTS",
          title: t("common:student"),
          width: columnWidth,
          isSortable: true,
          sortKey: STUDENT_FULL_NAME,
          isEnabled: true,
        };
      case "DATE":
        return {
          id: "DATE",
          columnKey: "DATE",
          title: t("common:date"),
          width: columnWidth,
          isSortable: true,
          sortKey: ATTENDANCE_CONSTANTS.ATTENDANCE_DATE,
          isEnabled: true,
        };
      case "PERIOD":
        return {
          id: "PERIOD",
          columnKey: "PERIOD",
          title: t("common:period"),
          width: columnWidth,
          isSortable: false,
          isEnabled:
            !isAttendanceOnceADay && !isDailyAttendance && !isHomeroomClassTab,
        };
      case "CLASS":
        return {
          id: "CLASS",
          columnKey: "CLASS",
          title: t("common:class"),
          width: columnWidth,
          isSortable: true,
          sortKey: ATTENDANCE_CONSTANTS.TITLE,
          // Enable CLASS column for daily tab with courses OR for regular period-based attendance
          isEnabled:
            isHomeroomClassTab || (!isAttendanceOnceADay && !isDailyAttendance),
        };
      case "ATTENDANCE":
        return {
          id: "ATTENDANCE",
          columnKey: "ATTENDANCE",
          title: t("attendance:attendance"),
          width: columnWidth,
          isSortable: false,
          isEnabled: true,
        };
      case "NOTES":
        return {
          id: "NOTES",
          columnKey: "NOTES",
          title: t("common:note_plural"),
          width: `${notesColumnWidth}%`,
          isSortable: false,
          isEnabled: true,
        };
      case "EXCUSAL":
        return {
          id: "EXCUSAL",
          columnKey: "EXCUSAL",
          title: t("attendance:excusals"),
          width: columnWidth,
          isSortable: false,
          isEnabled: true,
        };
    }
  });

  return _.filter(headerColumns, ({ isEnabled }) => isEnabled);
};

/**
 * Creates skeleton rows for the bulk mark attendance grid to show loading state during pagination
 * @param {number} count - Number of skeleton rows to create (default: 2)
 * @returns {Array} Array of skeleton row objects for DataGrid
 */
export const getBulkMarkAttendanceSkeletonRows = (count = 2) => {
  return _.map(_.range(count), index => ({
    rowId: `skeleton_${index}`,
    columns: [
      // STUDENTS(0), DATE(1), PERIOD(2), CLASS(3), ATTENDANCE(4), NOTES(5), EXCUSAL(6)
      {
        cellType: "skeleton",
        isEditable: false,
      },
      {
        cellType: "skeleton",
        isEditable: false,
      },
      {
        cellType: "skeleton",
        isEditable: false,
      },
      {
        cellType: "skeleton",
        isEditable: false,
      },
      {
        cellType: "skeleton",
        isEditable: false,
      },
      {
        cellType: "skeleton",
        isEditable: false,
      },
      {
        cellType: "skeleton",
        isEditable: false,
      },
    ],
  }));
};

const getShowExcusalIcon = ({ excusals, period }) => {
  if (_.isEmpty(excusals)) {
    return {
      cellType: "label",
      value: "",
      isEditable: false,
      isDisabled: true,
    };
  }

  const excusal = _.head(excusals);

  const isPartialExcusal =
    _.get(excusal, "durationType") === ATTENDANCE_CONSTANTS.PARTIAL_DAY;

  if (!isPartialExcusal) {
    return {
      cellType: "icon",
      value: "EnvelopeOutlined",
      isEditable: false,
      isDisabled: true,
    };
  }

  const excusalPeriods = _.get(excusal, "periods", []);

  const excusalPeriodIds = _.map(excusalPeriods, "id");

  const isPeriodIncluded = _.includes(
    excusalPeriodIds,
    _.get(period, "id", null)
  );

  if (isPeriodIncluded) {
    return {
      cellType: "icon",
      value: "EnvelopeOutlined",
      isEditable: false,
      isDisabled: true,
    };
  }

  return {
    cellType: "label",
    value: "",
    isEditable: false,
    isDisabled: true,
  };
};

export const getBulkMarkAttendanceGridRows = ({
  // t,
  studentsForBulkAttendanceMarkingEdges,
  attendanceOptions,
  bulkAttendanceSelections = [],
  columnOrder,
  isAttendanceOnceADay,
  isDailyAttendance,
  isHomeroomClassTab = false,
  considerCourseAndPeriodId,
}) => {
  return _.map(
    studentsForBulkAttendanceMarkingEdges,
    ({ student, date, period, course, note, excusals, attendanceOption }) => {
      const { id, profileImage, staffRelation, coursesV2 } = student;

      // prepare student details
      const studentName = getFormattedUserName({
        user: student,
        type: "student",
      });

      // Create a unique row identifier for each attendance record
      // For daily tab with courses: include courseId to create unique rows per course
      let attendanceRecordId;
      if (isHomeroomClassTab && !considerCourseAndPeriodId) {
        attendanceRecordId = `${id}_${date}_${_.get(course, "id")}`;
      } else if (isAttendanceOnceADay || isDailyAttendance) {
        attendanceRecordId = `${id}_${date}`;
      } else {
        attendanceRecordId = `${id}_${date}_${_.get(course, "id")}_${_.get(
          period,
          "id"
        )}`;
      }

      // Find existing selection for this attendance record
      const existingSelection = _.find(bulkAttendanceSelections, {
        studentId: id,
        date,
        ...(considerCourseAndPeriodId && {
          periodId: _.get(period, "id", null),
          courseId: _.get(course, "id", null),
        }),
      });

      // Get selected attendance option for pre-populating dropdown
      // Priority: user selection > server data (already marked attendance)
      let selectedAttendanceOption = null;

      if (existingSelection) {
        // User has made a selection in the current session
        selectedAttendanceOption = _.find(attendanceOptions, {
          id: existingSelection.attendanceCodeId,
        });
      } else if (attendanceOption) {
        // Use already marked attendance from server
        selectedAttendanceOption = _.find(attendanceOptions, {
          id: _.get(attendanceOption, "id"),
        });
      }

      // Get note value from existing selection or use the original note
      // Priority: user-modified note > server note
      const noteValue =
        existingSelection && !_.isNil(existingSelection.note)
          ? existingSelection.note
          : note || "";

      const rowData = {
        rowId: attendanceRecordId,
        columns: _.map(columnOrder, columnKey => {
          switch (columnKey) {
            case "STUDENTS":
              return {
                cellType: "label",
                value: studentName,
                styles: { gap: 8 },
                prefix: [
                  {
                    type: "avatar",
                    name: getInitials({ value: studentName }),
                    src: profileImage,
                    color: getDataGridRandomAvatarColor(id),
                    shape: "square",
                    borderRadius: "5",
                    theme: "light",
                    size: 28,
                  },
                ],
                isEditable: false,
              };
            case "DATE":
              return {
                cellType: "label",
                value: date,
                isEditable: false,
              };
            case "PERIOD":
              return {
                cellType: "label",
                value: _.get(period, "label"),
                isEditable: false,
              };
            case "CLASS":
              return {
                cellType: "label",
                value: _.get(course, "title"),
                isEditable: false,
              };
            case "ATTENDANCE": {
              return {
                cellType: "dropdown",
                value: selectedAttendanceOption
                  ? [selectedAttendanceOption]
                  : [],
                placeholder: "-",
                validInputs: {
                  options: attendanceOptions,
                },
                isEditable: true,
                addNewOptionEnabled: false,
              };
            }
            case "NOTES":
              return {
                cellType: "text",
                value: noteValue,
                isEditable: true,
              };
            case "EXCUSAL":
              // Show excusal envelope icon if student has excusals
              return getShowExcusalIcon({ excusals, period });
          }
        }),
      };

      return rowData;
    }
  );
};

export const bulkActionToolbarStyles = {
  position: "fixed",
  bottom: "20px",
  left: "50%",
  transform: "translateX(-50%)",
  zIndex: 900,
};

export const getTimetableEnabledCurriculumProgramIds = ({
  organizationCurriculumPrograms,
}) => {
  const allCurriculumProgramIds = _.map(
    organizationCurriculumPrograms,
    ({ id }) => id
  );

  return _.reduce(
    allCurriculumProgramIds,
    (acc, curriculumProgramId) => {
      if (
        ACLStorage.getPermissionValueByContext(
          CAN_VIEW_TIMETABLE_MODULE_PERMISSION,
          [{ entityType: "CURRICULUM_PROGRAM", entityId: curriculumProgramId }]
        )
      ) {
        acc.push(curriculumProgramId);
      }
      return acc;
    },
    []
  );
};
